import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { clientApi, MeResp, WorkspaceDetail as WorkspaceDetailType, WorkspaceExecuteResp } from '@/api/client'
import { qk } from '@/api/queryKeys'
import DownloadReportButton from '@/components/DownloadReportButton'
import ErrorBox from '@/components/ErrorBox'
import WorkspaceStateNotice from '@/components/WorkspaceStateNotice'
import Skeleton from '@/components/Skeleton'
import Topbar from '@/components/Topbar'
import Freshness from '@/components/Freshness'
import ProbBar from '@/components/ProbBar'
import { compactWorkspaceTitle } from '@/utils/workspaceDisplay'
import { cleanSnippet, fillEntityPlaceholders, fmtProbInt, freshest, timeAgo, fmtTs } from '@/utils/format'

// "Ask about this read" — the conversational layer on the dashboard. v1
// routes to the existing single-ask machinery: the question lands on the
// workspace's own watchlist (so it lives with the workspace's items) and the
// answer links back here. Deep grounding in the workspace's own forecasts is
// the post-trial ask-the-read step.
function WorkspaceAskBox({ workspaceId, watchlistId, workspaceTitle }: { workspaceId: string; watchlistId?: string | null; workspaceTitle?: string }) {
  const [question, setQuestion] = useState('')
  const [phase, setPhase] = useState<'idle' | 'running' | 'done' | 'failed'>('idle')
  const [result, setResult] = useState<{ itemId?: string; prob?: number; error?: string }>({})

  async function submit() {
    const q = question.trim()
    if (!q || phase === 'running') return
    setPhase('running')
    setResult({})
    try {
      const resp = await clientApi.askQuestion(q, watchlistId || undefined, null, {
        source: 'workspace_ask',
        workspace_id: workspaceId,
      } as any)
      const itemId = resp.item_id
      const deadline = Date.now() + 12 * 60 * 1000
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000))
        const st = await clientApi.jobStatus(resp.job_id)
        if (st.status === 'succeeded') {
          setResult({ itemId, prob: st.output_json?.probability ?? undefined })
          setPhase('done')
          return
        }
        if (st.status === 'failed') {
          setResult({ itemId, error: st.error || 'Forecast failed' })
          setPhase('failed')
          return
        }
      }
      setResult({ itemId, error: 'Still running — check back from the item page.' })
      setPhase('failed')
    } catch (e: any) {
      setResult({ error: e?.message || 'Failed to submit question' })
      setPhase('failed')
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 850 }}>Ask about this read</div>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <input
          className="input"
          style={{ flex: 1 }}
          placeholder="Ask a follow-up forecast question — include the company and timeframe…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void submit() }}
          disabled={phase === 'running'}
        />
        <button className="btn primary" onClick={() => void submit()} disabled={phase === 'running' || !question.trim()}>
          {phase === 'running' ? 'Forecasting…' : 'Ask'}
        </button>
      </div>
      {phase === 'running' ? <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>Running a full source-backed forecast — usually a few minutes. You can leave this page; the answer lands with this workspace's items.</p> : null}
      {phase === 'done' && result.itemId ? (
        <p style={{ marginTop: 8, fontSize: 14 }}>
          Answer: <strong>{typeof result.prob === 'number' ? `${Math.round(result.prob * 100)}%` : 'ready'}</strong>{' '}
          <Link to={`/items/${result.itemId}`} state={{ from: { kind: 'workspace', id: workspaceId, title: workspaceTitle || 'Workspace', path: `/workspaces/${workspaceId}` } }}>
            View the full answer →
          </Link>
        </p>
      ) : null}
      {phase === 'failed' ? <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>{result.error}{result.itemId ? <> · <Link to={`/items/${result.itemId}`}>open item</Link></> : null}</p> : null}
    </div>
  )
}

function statusLabel(status?: string | null) {
  const key = String(status || 'unknown').toLowerCase()
  if (key === 'early_source_backed_signal') return 'Early source-backed signal'
  const labels: Record<string, string> = {
    active_monitoring: 'Live monitoring',
    active: 'Live',
    external_ready: 'Ready to share',
    external_distribution_ready: 'Ready to share',
    ready_for_external_distribution: 'Ready to share',
    // The readiness pipeline's stage names are Cenva-internal workflow
    // vocabulary ("internal review") from the analyst-in-the-loop era. A
    // client building their own workspace gets their journey instead:
    // Building -> Needs attention -> Ready -> Live.
    internal_review_ready: 'Ready',
    internal_ready: 'Ready',
    not_ready_for_internal_use: 'Needs attention',
    reviewed_not_ready: 'Needs attention',
    publishable: 'Ready to share',
    not_active: 'Not active',
    no_changes_yet: 'No changes yet',
    changes_detected: 'Changes detected',
    layer_1_batch_ready: 'Ready for forecast run',
    batch_submitted: 'Forecasts running',
    answer_ready: 'Current read ready',
    answered: 'Current read ready',
    answered_with_caveats: 'Current read ready',
  }
  if (labels[key]) return labels[key]
  return clientFacingText(String(status || 'unknown').replaceAll('_', ' '))
}

// Local `pct` is delegated to the shared `fmtProbInt` so probabilities have
// one rounding policy across the dashboard. Kept here as a thin alias to
// avoid a sweeping rename across this large file.
function pct(value: any) {
  return fmtProbInt(value) === '-' ? 'n/a' : fmtProbInt(value)
}

function fmtDate(value?: string | null) {
  if (!value) return 'n/a'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString()
}

function rankedForecastQuestionsFrom(layer2: any, layer3: any) {
  const answerRows = layer3?.client_answer?.ranked_forecast_questions
  if (Array.isArray(answerRows) && answerRows.length) return answerRows
  const synthesisRows = layer2?.workspace_synthesis?.ranked_primitives
  if (Array.isArray(synthesisRows) && synthesisRows.length) return synthesisRows
  const evidenceRows = layer2?.evidence_map?.ranked_forecast_questions
  if (Array.isArray(evidenceRows) && evidenceRows.length) return evidenceRows
  return []
}

function agentSourceLabelCount(items?: any[]) {
  return (items || []).reduce((total, item) => {
    const output = item?.forecast_output_json || {}
    const agents = Array.isArray(output?.agent_outputs) ? output.agent_outputs : []
    return total + agents.reduce((n: number, agent: any) => n + (Array.isArray(agent?.sources) ? agent.sources.filter(Boolean).length : 0), 0)
  }, 0)
}

function forecastQuestionLabel(ref: any) {
  return clientSignalLabel(ref?.display_label || ref?.driver || ref?.entity_name || 'Forecast question')
}

function clientFacingText(value: any) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\bby\s+workspace[-_ ]+(?:layer\s*\d+[-_ ]*)?snapshot[-_ ][0-9A-Za-z:_-]+\s+plus\s+(\d+)\s+months\b/gi, 'within $1 months of the workspace start date')
    .replace(/\bworkspace[-_ ]+(?:layer\s*\d+[-_ ]*)?snapshot[-_ ][0-9A-Za-z:_-]+\s+plus\s+(\d+)\s+months\b/gi, 'the $1-month workspace horizon')
    .replace(/\bworkspace[-_ ]+(?:layer\s*\d+[-_ ]*)?snapshot[-_ ][0-9A-Za-z:_-]+\b/gi, 'the workspace start date')
    .replace(/Already_Priced_In_Falsifier/gi, 'already priced-in risk signal')
    .replace(/already_priced_in_falsifier/gi, 'already priced-in risk signal')
    .replace(/low_priority_or_falsifier_watch/gi, 'lower-priority risk watch')
    .replace(/falsifier_case/gi, 'risk case')
    .replace(/\bfalsifiers\b/gi, 'risk signals')
    .replace(/\bfalsifier\b/gi, 'risk signal')
    .replace(/\bfalsifying\b/gi, 'weakening')
    .replace(/\bfalsifies\b/gi, 'weakens')
    .replace(/\bfalsified\b/gi, 'weakened')
    .replace(/\bfalsify\b/gi, 'weaken')
    .replace(/\bfalsification\b/gi, 'weakening signal')
    .replace(/\btrade recommendations?\b/gi, 'portfolio instruction')
    .replace(/\bactionable routes?\b/gi, 'available public-market route')
}

function clientSignalLabel(value: any) {
  let text = clientFacingText(value)
  const lower = text.toLowerCase()
  if (lower.includes('cowos') || lower.includes('advanced packaging')) return 'Advanced-packaging demand signal'
  if (lower.includes('hpc') && lower.includes('advanced-node')) return 'HPC/AI advanced-node demand signal'
  if (lower.includes('constraint') && !lower.includes('demand')) text = text.replace(/constraints?/gi, match => match.charAt(0) === match.charAt(0).toUpperCase() ? 'Demand signal' : 'demand signal')
  return text
}

function looksLikeFalsifier(ref: any) {
  const text = `${forecastQuestionLabel(ref)} ${ref?.question_text || ''}`.toLowerCase()
  return /\b(falsifier|weaken|weakening|pressure|delay|burden|risk|lower|reduce|below|offset|deterioration)\b/.test(text)
}

function forecastItemHref(itemOrRef: any, itemBySetItemId?: Map<string, any>) {
  const direct = String(itemOrRef?.item_id || '').trim()
  if (direct) return `/items/${direct}`
  const setItemId = String(itemOrRef?.set_item_id || '').trim()
  const itemId = setItemId ? String(itemBySetItemId?.get(setItemId)?.item_id || '').trim() : ''
  return itemId ? `/items/${itemId}` : ''
}

function candidateKey(candidate: any, index = 0) {
  const ticker = String(candidate?.ticker || '').toUpperCase().trim()
  if (ticker) return `ticker:${ticker}`
  return `name:${String(candidate?.name || candidate?.entity_name || index).trim().toLowerCase()}`
}

function graphView(graph: any, type: string) {
  const views = Array.isArray(graph?.views) ? graph.views : []
  return views.find((view: any) => view?.type === type) || null
}

function cockpitSourceLabel(value?: string | null) {
  const key = String(value || '').toLowerCase()
  if (key === 'source_backed') return 'Source-backed'
  if (key === 'partial_source_coverage') return 'Partial sources'
  if (key === 'agent_label_only') return 'Source labels only'
  if (key === 'needs_sources') return 'Needs sources'
  return statusLabel(value)
}

function readLabelClass(value?: string | null) {
  const key = String(value || '').toLowerCase()
  // Phase B: canonical LLM-written labels (workspace_memo_writer ->
  // cockpit object reads). Keep the legacy heuristic keys below — they
  // still apply when the memo writer hasn't run.
  if (key === 'clearer thesis' || key === 'strengthening') return 'pill ok'
  if (key === 'weakening' || key === 'falling out of frame') return 'pill bad'
  if (key === 'mixed evidence' || key === 'watch for next print' || key === 'insufficient signal') return 'pill warn'
  // Legacy heuristic vocab (snake_case from _object_read_label).
  if (key.includes('low_confidence')) return 'pill warn'
  if (key.includes('support') || key.includes('constructive')) return 'pill ok'
  if (key.includes('weakening') || key.includes('weak')) return 'pill bad'
  return 'pill warn'
}

function companyIntelStatusClass(value?: string | null) {
  const key = String(value || '').toLowerCase()
  if (['ready', 'available', 'published', 'completed', 'active'].includes(key)) return 'pill ok'
  if (['failed', 'error', 'track_failed', 'company_registry_failed'].includes(key)) return 'pill bad'
  return 'pill warn'
}

function workspaceCompanyIntelRows(summary: any) {
  const intel = summary?.workspace_company_intel
  return Array.isArray(intel?.companies) ? intel.companies.filter((row: any) => row && typeof row === 'object') : []
}

function resolveCompanyIntel(object: any, rows: any[]) {
  const inline = object?.company_intel && typeof object.company_intel === 'object' ? object.company_intel : {}
  const companyId = object?.company_id || inline?.company_id
  const ticker = String(object?.ticker || inline?.ticker || '').toUpperCase().trim()
  const matched = rows.find((row: any) => {
    const rowTicker = String(row?.ticker || '').toUpperCase().trim()
    return (companyId && String(row?.company_id || '') === String(companyId)) || (ticker && rowTicker === ticker)
  })
  const out = { ...inline, ...(matched || {}) }
  return Object.keys(out).length ? out : null
}

function shortText(value: any, limit = 230) {
  const text = String(value || '').replace(/\s+/g, ' ').trim()
  if (!text || text.length <= limit) return text
  return `${text.slice(0, Math.max(0, limit - 1)).trim()}...`
}

function fmtSignalScore(value: any) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'n/a'
  const score = value <= 1 ? Math.round(value * 100) : Math.round(value)
  return `${Math.max(0, Math.min(100, score))}/100`
}

function fmtRankedValue(value: any, semantics?: any) {
  return String(semantics || '') === 'event_probability' ? pct(value) : fmtSignalScore(value)
}

function eventTitle(event: any) {
  return clientFacingText(event?.title || event?.label || String(event?.event || 'Workspace update').replaceAll('_', ' '))
}

function eventSummary(event: any) {
  const probability = typeof event?.probability === 'number' ? ` Event probability: ${pct(event.probability)}.` : ''
  const delta = typeof event?.delta === 'number' ? ` Change: ${event.delta > 0 ? '+' : ''}${Math.round(event.delta * 100)} pts.` : ''
  return clientFacingText(`${event?.summary || event?.description || event?.label || ''}${probability}${delta}`.trim())
}

function objectBenefitText(row: any) {
  return clientFacingText(row?.summary || row?.mechanism || row?.top_signal || row?.read_label || 'Supporting signal still developing.')
}

function mergedChangeEvents(artifact: any, graphChange: any) {
  const rows = [
    ...(Array.isArray(artifact?.events) ? artifact.events : []),
    ...(Array.isArray(graphChange?.events) ? graphChange.events : []),
  ].filter(Boolean)
  const seen = new Set<string>()
  const deduped = rows.filter((row: any) => {
    const key = `${row?.at || ''}|${row?.event || ''}|${row?.question_id || ''}|${row?.set_item_id || ''}|${row?.label || row?.title || ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return deduped.sort((a: any, b: any) => new Date(b?.at || 0).getTime() - new Date(a?.at || 0).getTime())
}

function WorkspaceSignalSemantics({
  forecastCount,
  totalChecks,
  verifiedSourceRecords,
  agentSourceLabels,
  readinessReady,
  externalReady,
  activeMonitoring,
  latestUpdateAt,
  readinessScore,
}: {
  forecastCount: number
  totalChecks: number
  verifiedSourceRecords: number
  agentSourceLabels: number
  readinessReady: boolean
  externalReady: boolean
  activeMonitoring: boolean
  latestUpdateAt?: string | null
  readinessScore?: any
}) {
  const sourceCount = verifiedSourceRecords || agentSourceLabels || 0
  return (
    <div className="workspace-indicator-grid" style={{ marginTop: 16 }}>
      <div className="workspace-indicator-card primary">
        <span>Event probabilities</span>
        <strong>{forecastCount}/{totalChecks || forecastCount || 0}</strong>
        <small>Forecasts estimate whether each observable event happens by the horizon. They are not source-quality scores.</small>
      </div>
      <div className="workspace-indicator-card">
        <span>Signal quality</span>
        <strong>{fmtSignalScore(readinessScore?.score)}</strong>
        <small>This summarizes source coverage and usefulness. It is not an event probability.</small>
      </div>
      <div className="workspace-indicator-card">
        <span>Source coverage</span>
        <strong>{sourceCount}</strong>
        <small>{verifiedSourceRecords ? 'Verified source records attached.' : agentSourceLabels ? 'Forecast source labels only; verified URLs still needed.' : 'No source records attached yet.'}</small>
      </div>
      <div className="workspace-indicator-card">
        <span>Freshness</span>
        <strong>{latestUpdateAt ? timeAgo(latestUpdateAt) : 'n/a'}</strong>
        <small>{latestUpdateAt ? `Latest workspace update ${fmtTs(latestUpdateAt)}` : 'No timestamped update yet.'}</small>
      </div>
      <div className="workspace-indicator-card">
        <span>Sharing state</span>
        <strong>{externalReady ? 'Ready to share' : readinessReady ? 'Ready' : 'Checks pending'}</strong>
        <small>{activeMonitoring ? 'Monitoring is active.' : 'Monitoring can be activated after review.'}</small>
      </div>
    </div>
  )
}

function WorkspaceWhatChanged({ artifact, graphChange }: { artifact: any; graphChange: any }) {
  const events = mergedChangeEvents(artifact, graphChange)
  const latest = events[0]
  const headline = artifact?.headline || (latest ? eventTitle(latest) : 'No monitored changes yet')
  const summary = artifact?.summary || (latest ? eventSummary(latest) : 'No monitor or forecast changes have been recorded for this workspace yet.')
  return (
    <div className="card">
      <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>What changed</div>
      <h2 style={{ marginTop: 6 }}>{headline}</h2>
      <p className="muted">{summary}</p>
      <div className="row" style={{ gap: 6, marginTop: 10 }}>
        <span className={artifact?.status === 'changes_detected' ? 'pill ok' : 'pill'}>{statusLabel(artifact?.status || 'no_changes_yet')}</span>
        {artifact?.latest_update_at ? <span className="pill">latest {timeAgo(artifact.latest_update_at)}</span> : null}
        {artifact?.active_monitor_count ? <span className="pill ok">{artifact.active_monitor_count} active monitors</span> : null}
      </div>
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {events.slice(0, 5).map((event: any, i: number) => (
          <div key={`${event?.at || i}-${event?.event || 'event'}`} className="workspace-question-row">
            <span className={i === 0 ? 'pill ok' : 'pill'}>{event?.at ? timeAgo(event.at) : 'update'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 750 }}>{eventTitle(event)}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{eventSummary(event) || 'Workspace state updated.'}</div>
            </div>
          </div>
        ))}
        {!events.length ? <div className="muted">Activate monitoring after review to start recording source, forecast, and read changes.</div> : null}
      </div>
    </div>
  )
}

function WorkspaceMonitoringTimeline({
  lifecycle,
  artifact,
  canActivateMonitoring,
  executing,
  onActivate,
}: {
  lifecycle: any
  artifact: any
  canActivateMonitoring: boolean
  executing: boolean
  onActivate: () => void
}) {
  const targets = Array.isArray(lifecycle?.monitor_targets) ? lifecycle.monitor_targets : []
  const active = lifecycle?.status === 'active_monitoring'
  return (
    <div className="card">
      <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>Monitoring timeline</div>
      <h2 style={{ marginTop: 6 }}>{active ? 'Live monitoring is active' : canActivateMonitoring ? 'Ready to activate monitoring' : 'Monitoring not active yet'}</h2>
      <p className="muted">{active ? 'Cenva will refresh the read when monitored public evidence changes.' : 'Monitoring turns the reviewed read into a living dashboard. It is separate from forecast probabilities and source quality.'}</p>
      <div className="row" style={{ gap: 6, marginTop: 10 }}>
        <span className={active ? 'pill ok' : 'pill'}>{statusLabel(lifecycle?.status || 'not_active')}</span>
        <span className="pill">{targets.length || artifact?.active_monitor_count || 0} monitor targets</span>
        {lifecycle?.next_monitor_run_at || artifact?.next_monitor_run_at ? <span className="pill ok">next check {fmtTs(lifecycle?.next_monitor_run_at || artifact?.next_monitor_run_at)}</span> : null}
      </div>
      {!active && canActivateMonitoring ? (
        <button className="btn primary" style={{ marginTop: 12 }} onClick={onActivate} disabled={executing}>{executing ? 'Activating monitoring...' : 'Activate monitoring'}</button>
      ) : null}
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {targets.slice(0, 5).map((target: any, i: number) => (
          <div key={target?.monitor_id || target?.set_item_id || i} className="workspace-question-row">
            <span className={target?.status === 'active' ? 'pill ok' : 'pill'}>{statusLabel(target?.status || 'planned')}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 750 }}>{target?.title || target?.entity_name || target?.driver || target?.ticker || 'Monitor target'}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{fillEntityPlaceholders(target?.monitor_rule, target?.entity_name || target?.title, target?.ticker) || 'Refresh when public evidence changes this signal check.'}</div>
              {target?.next_run_at ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>Next check {fmtTs(target.next_run_at)}</div> : null}
            </div>
          </div>
        ))}
        {!targets.length ? <div className="muted">No monitor targets have been activated yet.</div> : null}
      </div>
    </div>
  )
}

export function workspaceIsGenerating(data?: WorkspaceDetailType | null) {
  const summary = data?.question_set?.summary_json || {}
  if (summary?.workspace_layer3?.client_answer || summary?.workspace_lifecycle?.status === 'active_monitoring') return false
  const execution = summary?.workspace_execution || {}
  const status = String(data?.status || execution?.status || '').toLowerCase()
  // Async layer-0 scoping in flight counts as generating so the detail
  // poll keeps refreshing until the worker lands the signal map.
  const scopingStatus = String(summary?.workspace_layer0_scoping?.status || '').toLowerCase()
  return Boolean(
    ['queued', 'running'].includes(scopingStatus) ||
    summary?.workspace_layer1_preparation ||
    (data?.items?.length || 0) > 0 ||
    ['layer_1_ready', 'materialized', 'layer_1_batch_ready', 'batch_submitted', 'forecasting', 'forecasted', 'layer_2_ready'].includes(status) ||
    ['layer_1_batch_ready', 'batch_submitted', 'forecasting', 'forecasted', 'layer_2_ready'].includes(String(execution?.status || '').toLowerCase()),
  )
}

export function workspaceCanActivateMonitoring(data?: WorkspaceDetailType | null) {
  const summary = data?.question_set?.summary_json || {}
  if (summary?.workspace_lifecycle?.status === 'active_monitoring') return false
  if (!summary?.workspace_layer3?.client_answer) return false
  const review = summary?.workspace_readiness_review || summary?.workspace_publication_review || {}
  const ready = review?.ready_for_internal_use === true || review?.publishable === true
  if (!ready) return false
  const items = Array.isArray(data?.items) ? data.items : []
  const generated = Number(data?.question_set?.generated_count || items.length || 0)
  const itemForecasted = items.filter((item: any) => {
    const status = String(item?.status || '').toLowerCase()
    const output = item?.forecast_output_json || {}
    return status === 'succeeded' || status === 'forecasted' || output?.execution_state === 'forecasted'
  }).length
  const forecasted = Math.max(Number(data?.question_set?.forecasted_count || 0), itemForecasted)
  return generated > 0 && forecasted >= generated
}

function buildProgressSteps(data?: WorkspaceDetailType | null) {
  const summary = data?.question_set?.summary_json || {}
  const execution = summary?.workspace_execution || {}
  const layer0 = summary?.workspace_layer0
  const prep = summary?.workspace_layer1_preparation
  const layer2 = summary?.workspace_layer2
  const layer3 = summary?.workspace_layer3
  const lifecycle = summary?.workspace_lifecycle
  const generated = Number(data?.question_set?.generated_count || data?.items?.length || 0)
  const forecasted = Number(data?.question_set?.forecasted_count || 0)
  const batchStatus = execution?.batch_status || execution?.status
  const candidateDiscoveryStatus = String(layer0?.candidate_discovery?.status || '')
  const entityShortlistNotNeeded = candidateDiscoveryStatus === 'not_applicable_non_entity_workspace'
  const opportunitySpaceResearch = String(layer0?.research_branch?.mode || '') === 'opportunity_space_research'
  const rankedForecastQuestions = rankedForecastQuestionsFrom(layer2, layer3)
  const isMonitorWorkspace = entityShortlistNotNeeded || layer3?.client_answer?.answer_type === 'monitor_prioritization' || ['indicator_dashboard', 'scenario_monitor'].includes(String(summary?.workspace_draft?.workspace_intent?.entity_mode || ''))
  const rows = [
    {
      key: 'understand',
      title: 'Understanding the question',
      subtitle: 'Framing the horizon, entities, drivers, and implied scenario.',
      done: Boolean(summary?.workspace_draft),
      metric: summary?.workspace_draft?.workspace_type ? statusLabel(summary.workspace_draft.workspace_type) : 'framing',
      detail: 'Cenva turns the blank question into a workspace frame: what kind of read is needed, which drivers matter, what horizon is implied, and what should be forecastable rather than hand-waved.',
    },
    {
      key: 'scope',
      title: entityShortlistNotNeeded ? 'Defining the indicator map' : opportunitySpaceResearch ? 'Testing opportunity space' : 'Building the candidate universe',
      subtitle: entityShortlistNotNeeded ? 'Turning the decision frame into macro/economic signal checks.' : opportunitySpaceResearch ? 'Checking whether the theme is externally measurable before ranking companies.' : 'Screening candidates and creating a shortlist to test.',
      done: layer0?.status === 'scoping_ready',
      metric: entityShortlistNotNeeded ? 'no shortlist needed' : opportunitySpaceResearch ? 'space-level tests' : (layer0?.top_10_shortlist?.length ? `${layer0.top_10_shortlist.length} shortlisted` : (layer0?.status === 'scoping_ready' ? '0 shortlisted' : 'screening…')),
      detail: entityShortlistNotNeeded ? 'This workspace is driven by economic indicators and scenario triggers, so Cenva skips company shortlisting and builds forecast questions directly from the decision frame.' : opportunitySpaceResearch ? 'Candidate discovery was thin, so Cenva is first creating forecast questions that test whether the opportunity mechanism and public-company exposure are externally measurable.' : 'This stage maps possible candidates, checks existing Cenva coverage, and creates the top shortlist that will become the evidence base for the workspace read.',
    },
    {
      key: 'sources',
      title: isMonitorWorkspace ? 'Setting signal rules' : 'Gathering evidence',
      subtitle: isMonitorWorkspace ? 'Defining the public source categories that can resolve each monitor.' : 'Attaching source packs and benchmark snapshots before forecasts run.',
      done: prep?.status === 'ready_for_layer_1_forecasts',
      metric: isMonitorWorkspace ? 'external criteria' : `${prep?.verified_source_record_count || prep?.verified_source_count || 0} source records`,
      detail: isMonitorWorkspace ? 'For monitor workspaces, Cenva first pins each signal check to public artifacts such as advisories, research, filings, regulator actions, vendor disclosures, reputable press, or third-party datasets.' : 'The workspace attaches source packs, public proxies, SEC/company evidence, and benchmark snapshots so the eventual ranking has a concrete basis instead of generic model context.',
    },
    {
      key: 'primitives',
      title: 'Creating forecast questions',
      subtitle: entityShortlistNotNeeded ? 'Breaking the broad question into measurable indicators, scenarios, and decision triggers.' : opportunitySpaceResearch ? 'Creating space-level signal checks before candidate-specific forecasts.' : 'Breaking the broad question into measurable signal checks.',
      done: generated > 0,
      metric: `${generated} questions`,
      detail: entityShortlistNotNeeded ? 'Each forecast question tests an observable macro or operating trigger that can change the workspace read.' : 'Each candidate gets measurable forecast questions so the final ranking can explain why each candidate scores where it does.',
    },
    {
      key: 'forecasts',
      title: 'Running forecasts',
      subtitle: 'Waiting for probability estimates and evidence diagnostics to complete.',
      done: generated > 0 && forecasted >= generated,
      metric: generated ? `${forecasted}/${generated} forecasted` : String(batchStatus || 'pending'),
      detail: 'The slow part is the actual forecast work. Each question is estimated independently, then results are parsed into probabilities, evidence diagnostics, and history links.',
    },
    {
      key: 'derive',
      title: isMonitorWorkspace ? 'Ranking signal checks' : 'Ranking candidates',
      subtitle: isMonitorWorkspace ? 'Combining forecasts into a prioritized monitor map.' : 'Combining forecast questions into ranked candidates and thesis cards.',
      done: layer2?.status === 'ready_for_layer_3_synthesis',
      metric: isMonitorWorkspace ? `${rankedForecastQuestions.length || 0} ranked` : `${layer2?.opportunity_map?.ranked_entities?.length || 0} ranked`,
      detail: isMonitorWorkspace ? 'Cenva turns the probability estimates into ranked monitor triggers so the broad operating question has an auditable evidence trail.' : 'Cenva turns the probability estimates into a ranked opportunity map, scenario rollups, and thesis cards. This is where the broad read starts becoming visible.',
    },
    {
      key: 'read',
      title: 'Writing the workspace read',
      subtitle: 'Creating the read panel, candidate cards, evidence trail, and caveats.',
      done: Boolean(layer3?.client_answer),
      metric: layer3?.client_answer ? 'read ready' : 'drafting',
      detail: 'The read is assembled from the ranking and keeps links back to the underlying forecast questions so users can drill from the top-line ranking into the history and evidence behind it.',
    },
    {
      key: 'monitors',
      title: lifecycle?.status === 'active_monitoring' ? 'Live monitoring active' : 'Optional live monitoring',
      subtitle: lifecycle?.status === 'active_monitoring' ? 'Refresh triggers are active for source, forecast, and read updates.' : 'The read can be reviewed before live refresh triggers are activated.',
      done: lifecycle?.status === 'active_monitoring',
      metric: `${lifecycle?.active_monitor_count || 0} monitors`,
      detail: isMonitorWorkspace ? 'Live monitoring can refresh the ranked signal checks when new public reports, advisories, regulatory records, vendor disclosures, or research change the setup.' : 'Once ready, the workspace becomes living: source packs, forecast questions, and ranked reads can be refreshed when earnings, filings, guidance, or estimate revisions change the setup.',
    },
  ]
  const firstOpen = rows.findIndex((row) => !row.done)
  return rows.map((row, idx) => ({
    ...row,
    state: row.done ? 'complete' : idx === firstOpen ? 'running' : 'pending',
  }))
}

function WorkspaceBuildProgress({ data, selectedKey, onSelect, activeAction }: { data: WorkspaceDetailType; selectedKey: string; onSelect: (key: string) => void; activeAction?: 'scoping' | 'building' }) {
  const summary = data?.question_set?.summary_json || {}
  const steps = buildProgressSteps(data)
  const running = steps.find((step) => step.state === 'running') || steps[steps.length - 1]
  const selected = steps.find((step) => step.key === selectedKey) || running || steps[0]
  const completeCount = steps.filter((step) => step.state === 'complete').length
  const activeBoost = activeAction === 'scoping' ? 0.5 : activeAction === 'building' ? 0.75 : 0
  const progress = Math.max(5, Math.round(((completeCount + activeBoost) / Math.max(1, steps.length)) * 100))
  const candidates = summary?.workspace_layer0?.top_10_shortlist || []
  const ranked = summary?.workspace_layer2?.opportunity_map?.ranked_entities || []
  const signalObjects = Array.isArray(summary?.workspace_layer0?.signal_map?.objects) ? summary.workspace_layer0.signal_map.objects : []
  const execution = summary?.workspace_execution || {}
  const entityShortlistNotNeeded = String(summary?.workspace_layer0?.candidate_discovery?.status || '') === 'not_applicable_non_entity_workspace'
  const opportunitySpaceResearch = String(summary?.workspace_layer0?.research_branch?.mode || '') === 'opportunity_space_research'
  // All forecasts in but no read yet: the workspace is in the brief window
  // between the last forecast landing and the ranking/read being synthesized.
  // Show a clear "assembling the read" state so it doesn't read as stuck on the
  // "Ranking candidates" step (the synthesis runs server-side and this page
  // updates when it lands; the worker's finalize sweep guarantees it does).
  const genCount = Number(data?.question_set?.generated_count || 0)
  const fcCount = Number(data?.question_set?.forecasted_count || 0)
  const hasRead = Boolean(summary?.workspace_layer3?.client_answer)
  const assemblingRead = !activeAction && genCount > 0 && fcCount >= genCount && !hasRead
  const headline = activeAction === 'scoping' ? 'Preparing signal map' : activeAction === 'building' ? 'Building workspace' : assemblingRead ? 'Forecasts complete — assembling your read' : (running?.title || 'Generating workspace')
  const description = activeAction === 'scoping'
    ? 'Cenva is checking the workspace frame, screening candidates, and turning the question into externally resolvable signal checks. Candidate screens typically take several minutes. You can leave this page; it updates as the signal map lands.'
    : activeAction === 'building'
      ? 'Cenva is preparing source packs, materializing forecast questions, and starting the forecast run. You can leave this page; it will keep updating as artifacts land.'
      : assemblingRead
        ? 'All forecasts are in. Cenva is now ranking the candidates and writing the read — this is the final step and usually takes a couple of minutes. You can leave this page; the read appears here as soon as it lands.'
        : 'This can take 20-40 minutes. You can leave this page; Cenva will keep building the workspace and this page will update as artifacts land.'

  return (
    <div className="workspace-build">
      <div className="workspace-build-main">
        <div className="workspace-build-orb" aria-hidden="true" />
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>{activeAction ? 'Working now' : 'Building workspace'}</div>
        <h2>{headline}</h2>
        <p className="muted">{description}</p>
        <div className={`workspace-build-progress ${activeAction ? 'active' : ''}`}><span style={{ width: `${progress}%` }} /></div>
        <div className="workspace-build-statusline">
          <span>{activeAction ? 'Working — this page updates automatically' : assemblingRead ? 'Assembling the read — this page updates automatically' : `${progress}% assembled`}</span>
          <span>{execution?.batch_id ? `Batch ${execution.batch_id}` : execution?.batch_status ? statusLabel(execution.batch_status) : activeAction === 'scoping' ? 'Screening universe' : assemblingRead ? 'Ranking + writing read' : 'Live build'}</span>
        </div>

        <div className="workspace-build-preview">
          <div>
            <strong>{entityShortlistNotNeeded ? 'Indicator map' : opportunitySpaceResearch ? 'Opportunity-space tests' : 'Candidate universe'}</strong>
            <div className="row" style={{ gap: 6, marginTop: 8 }}>
              {(entityShortlistNotNeeded && signalObjects.length ? signalObjects : (ranked.length ? ranked : candidates)).slice(0, 10).map((c: any, i: number) => <span key={`${c.signal_id || c.ticker || i}`} className="pill">{c.label || c.ticker || c.name || c.entity_name}</span>)}
              {!(ranked.length || candidates.length || signalObjects.length) ? <span className="muted">{entityShortlistNotNeeded ? 'No company shortlist required.' : opportunitySpaceResearch ? 'Testing the opportunity before ranking companies.' : activeAction === 'scoping' ? 'Screening the universe now — the full shortlist appears here at once when this step finishes (usually 2–5 min). No need to refresh.' : 'Waiting for shortlist...'}</span> : null}
            </div>
          </div>
          <div>
            <strong>Latest artifact</strong>
            <p className="muted" style={{ margin: '6px 0 0' }}>{selected.detail}</p>
          </div>
        </div>
      </div>

      <div className="workspace-build-steps">
        {steps.map((step) => (
          <button key={step.key} className={`workspace-build-step ${step.state} ${selected.key === step.key ? 'selected' : ''}`} onClick={() => onSelect(step.key)}>
            <span className="workspace-build-dot" />
            <span style={{ minWidth: 0 }}>
              <strong>{step.title}</strong>
              <small>{step.subtitle}</small>
            </span>
            <em>{step.metric}</em>
          </button>
        ))}
      </div>
    </div>
  )
}

function WorkspaceCockpitTestRow({
  test,
  itemBySetItemId,
  workspaceFrom,
}: {
  test: any
  itemBySetItemId: Map<string, any>
  workspaceFrom?: WorkspaceFrom
}) {
  const href = forecastItemHref(test, itemBySetItemId)
  const stance = String(test?.stance || '').toLowerCase()
  const weakens = stance === 'falsifier'
  const condition = shortText(test?.condition || test?.question || test?.monitor_rule || '')
  const linkState = workspaceFrom ? { from: workspaceFrom } : undefined
  return (
    <div className="workspace-cockpit-test-row">
      <span className={weakens ? 'pill warn' : 'pill ok'}>{weakens ? 'would weaken' : 'would support'}</span>
      <div style={{ minWidth: 0 }}>
        <strong>{href ? <Link to={href} state={linkState}>{test?.label || test?.driver || 'Forecast question'}</Link> : (test?.label || test?.driver || 'Forecast question')}</strong>
        <small>{test?.object_label ? `${test.object_label} · ` : ''}{condition}</small>
      </div>
      <em>{typeof test?.probability === 'number' ? `Event prob ${pct(test.probability)}` : 'No probability'}</em>
    </div>
  )
}

function buildCompanyInsight(object: any, companyIntel: any, rank = 0) {
  if (!companyIntel || typeof companyIntel !== 'object') return null
  const companyId = companyIntel?.company_id || object?.company_id
  const reportStatus = companyIntel?.report_status || (companyId ? 'available' : null)
  if (!companyId && !reportStatus) return null

  const posture = companyIntel?.forecast_posture && typeof companyIntel.forecast_posture === 'object' ? companyIntel.forecast_posture : {}
  const memoCall = companyIntel?.memo_call && typeof companyIntel.memo_call === 'object' ? companyIntel.memo_call : {}
  const mostLikelyCase = posture?.most_likely_case && typeof posture.most_likely_case === 'object' ? posture.most_likely_case : null
  const ticker = String(companyIntel?.ticker || object?.ticker || '').toUpperCase().trim()
  const companyName = companyIntel?.name || object?.label || ticker || 'Company'
  const headline = shortText(posture?.headline || memoCall?.headline || 'Source-backed company brief', 140)
  const read = shortText(posture?.assessment || memoCall?.summary || object?.read_summary || 'Company intelligence is being assembled from the workspace signal map.', 240)
  const proofPoint = shortText(posture?.next_proof_point || object?.leading_confirmers?.[0]?.label || object?.leading_confirmers?.[0]?.condition, 150)
  const riskSignal = shortText(posture?.dominant_risk || object?.leading_falsifiers?.[0]?.label || object?.leading_falsifiers?.[0]?.condition, 150)
  const caseLabel = shortText(mostLikelyCase?.label || mostLikelyCase?.case, 80)
  const signalChecks = [
    ...(Array.isArray(object?.leading_confirmers) ? object.leading_confirmers.slice(0, 2) : []),
    ...(Array.isArray(object?.leading_falsifiers) ? object.leading_falsifiers.slice(0, 1) : []),
  ]

  return {
    object,
    companyIntel,
    rank: rank + 1,
    companyId,
    reportStatus,
    ticker,
    companyName,
    headline,
    read,
    proofPoint,
    riskSignal,
    caseLabel,
    readLabel: posture?.label || statusLabel(object?.read_label || 'current read'),
    score: fmtSignalScore(object?.net_score),
    memoGeneratedAt: companyIntel?.memo_generated_at,
    refreshQueued: Boolean(companyIntel?.queued_job_id || companyIntel?.active_job_id),
    signalChecks,
  }
}

function WorkspaceCompanySignalDetails({
  checks,
  itemBySetItemId,
  workspaceFrom,
}: {
  checks: any[]
  itemBySetItemId: Map<string, any>
  workspaceFrom?: WorkspaceFrom
}) {
  const rows = Array.isArray(checks) ? checks.filter(Boolean) : []
  if (!rows.length) return null
  return (
    <details className="workspace-company-signal-details">
      <summary>{rows.length} signal checks</summary>
      <div className="workspace-company-signal-list">
        {rows.map((test: any, idx: number) => (
          <WorkspaceCockpitTestRow key={test?.id || idx} test={test} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />
        ))}
      </div>
    </details>
  )
}

function WorkspaceCompanyLeadCard({
  insight,
  itemBySetItemId,
  workspaceFrom,
}: {
  insight: any
  itemBySetItemId: Map<string, any>
  workspaceFrom?: WorkspaceFrom
}) {
  const linkState = workspaceFrom ? { from: workspaceFrom } : undefined

  return (
    <div className="workspace-company-lead-card">
      <div className="workspace-company-intel-top">
        <div style={{ minWidth: 0 }}>
          <span>Company Intelligence</span>
          <strong>{insight.companyName}</strong>
          {insight.ticker ? <small>{insight.ticker}</small> : null}
        </div>
        <div className="workspace-company-intel-actions">
          <span className={companyIntelStatusClass(insight.reportStatus)}>{statusLabel(insight.reportStatus || 'pending')}</span>
          {insight.companyId ? (
            <Link
              className="workspace-company-intel-cta"
              to={`/universe/companies/${insight.companyId}`}
              state={linkState}
            >
              Open Company Brief
            </Link>
          ) : null}
        </div>
      </div>
      <div className="workspace-company-lead-body">
        <div>
          <p><b>{insight.headline}</b>{insight.read && insight.read !== insight.headline ? ` ${insight.read}` : ''}</p>
          <WorkspaceCompanySignalDetails checks={insight.signalChecks} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />
        </div>
        <div className="workspace-company-intel-grid">
          <div>
            <span>Source-backed read</span>
            <strong>{insight.readLabel}</strong>
          </div>
          <div>
            <span>Case outlook</span>
            <strong>{insight.caseLabel || (insight.companyIntel?.memo_artifact_id ? 'Case forecast pending' : 'Brief pending')}</strong>
          </div>
          {insight.proofPoint ? <div><span>Next proof point</span><strong>{insight.proofPoint}</strong></div> : null}
          {insight.riskSignal ? <div><span>Risk signal</span><strong>{insight.riskSignal}</strong></div> : null}
        </div>
      </div>
      <div className="workspace-company-intel-footer">
        {insight.memoGeneratedAt ? (
          <Freshness label="Brief updated" iso={insight.memoGeneratedAt} staleAfterMs={7 * 24 * 3600 * 1000} />
        ) : (
          <span className="pill">Brief pending</span>
        )}
        {insight.refreshQueued ? <span className="pill">Refresh queued</span> : null}
      </div>
    </div>
  )
}

function WorkspaceCompanyCompactRow({
  insight,
  itemBySetItemId,
  workspaceFrom,
}: {
  insight: any
  itemBySetItemId: Map<string, any>
  workspaceFrom?: WorkspaceFrom
}) {
  const linkState = workspaceFrom ? { from: workspaceFrom } : undefined
  return (
    <div className="workspace-company-compare-row">
      <div className="workspace-company-compare-main">
        <span className="workspace-company-rank">#{insight.rank}</span>
        <div style={{ minWidth: 0 }}>
          <strong>{insight.companyName}</strong>
          <small>{insight.ticker || 'Company'} · score {insight.score}</small>
        </div>
      </div>
      <div className="workspace-company-compare-pillset">
        <span>{insight.readLabel}</span>
        <span>{insight.caseLabel || 'Case forecast pending'}</span>
      </div>
      <div className="workspace-company-compare-proof">
        {insight.proofPoint ? <span><b>Proof point</b>{insight.proofPoint}</span> : null}
        {insight.riskSignal ? <span><b>Risk signal</b>{insight.riskSignal}</span> : null}
      </div>
      <div className="workspace-company-compare-actions">
        {insight.companyId ? <Link className="workspace-company-intel-cta" to={`/universe/companies/${insight.companyId}`} state={linkState}>Open Brief</Link> : null}
      </div>
      <div className="workspace-company-compare-signals">
        <WorkspaceCompanySignalDetails checks={insight.signalChecks} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />
      </div>
    </div>
  )
}

function WorkspaceCompanyIntelligenceSection({
  objects,
  companyIntelRows,
  itemBySetItemId,
  workspaceFrom,
}: {
  objects: any[]
  companyIntelRows?: any[]
  itemBySetItemId: Map<string, any>
  workspaceFrom?: WorkspaceFrom
}) {
  const insights = (objects || [])
    .map((object: any, idx: number) => buildCompanyInsight(object, resolveCompanyIntel(object, companyIntelRows || []), idx))
    .filter(Boolean)
  if (!insights.length) return null
  const lead = insights[0]
  const comparisons = insights.slice(1)

  return (
    <section className="workspace-company-intel-section">
      <div className="workspace-company-section-header">
        <div>
          <span>Company Intelligence</span>
          <h3>{lead.companyName} leads the workspace read</h3>
          <p>{lead.headline}</p>
        </div>
        <div className="workspace-company-section-meta">
          <span className="pill ok">{insights.length} company briefs</span>
          <span className="pill">Forecast receipts collapsed</span>
        </div>
      </div>
      <WorkspaceCompanyLeadCard insight={lead} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />
      {comparisons.length ? (
        <div className="workspace-company-compare-list">
          {comparisons.map((insight: any) => (
            <WorkspaceCompanyCompactRow
              key={insight.companyId || insight.ticker || insight.rank}
              insight={insight}
              itemBySetItemId={itemBySetItemId}
              workspaceFrom={workspaceFrom}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}

function WorkspaceDisclosureSegment({
  title,
  subtitle,
  meta,
  children,
}: {
  title: string
  subtitle?: string
  meta?: string
  children: React.ReactNode
}) {
  return (
    <details className="workspace-disclosure-segment">
      <summary>
        <span>
          <strong>{title}</strong>
          {subtitle ? <small>{subtitle}</small> : null}
        </span>
        {meta ? <em>{meta}</em> : null}
      </summary>
      <div className="workspace-disclosure-body">{children}</div>
    </details>
  )
}

function WorkspaceAtAGlance({
  brief,
}: {
  brief: any
}) {
  const glance = brief?.at_a_glance && typeof brief.at_a_glance === 'object' ? brief.at_a_glance : {}
  const lead = glance?.lead_object && typeof glance.lead_object === 'object' ? glance.lead_object : {}
  const leadLabel = lead?.label ? `${lead.label}${lead?.ticker ? ` · ${lead.ticker}` : ''}` : 'No lead object yet'
  // No "Current read" tile: the cockpit renders directly under the hero
  // answer panel, so it repeated the same headline twice back-to-back.
  const tiles = [
    {
      label: 'Lead company',
      value: shortText(leadLabel, 110),
      detail: shortText(lead?.summary || 'Ranking pending.', 120),
      tone: 'primary',
    },
    {
      label: 'Strongest proof',
      value: shortText(glance?.strongest_proof || 'No strongest proof point selected yet.', 150),
      detail: 'Main signal behind the read.',
    },
    {
      label: 'Weak spot',
      value: shortText(glance?.weak_spot || 'No weakening signal selected yet.', 150),
      detail: glance?.could_flip_if ? `Could flip if: ${shortText(glance.could_flip_if, 90)}` : 'What limits conviction.',
    },
    {
      label: 'Evidence quality',
      value: shortText(glance?.evidence_quality || 'Source check pending', 150),
      detail: 'Source and agreement state.',
    },
  ]
  return (
    <div className="workspace-glance-strip">
      {tiles.map((tile) => (
        <div key={tile.label} className={tile.tone === 'primary' ? 'workspace-glance-card primary' : 'workspace-glance-card'}>
          <span>{tile.label}</span>
          <strong>{tile.value}</strong>
          <small>{tile.detail}</small>
        </div>
      ))}
    </div>
  )
}

function WorkspaceMemoList({ items }: { items: any[] }) {
  const rows = Array.isArray(items) ? items.filter(Boolean).slice(0, 5) : []
  if (!rows.length) return null
  return (
    <ul className="workspace-memo-list">
      {rows.map((item, i) => <li key={i}>{String(item)}</li>)}
    </ul>
  )
}

function WorkspaceMemoBrief({ sections, objects }: { sections: any; objects: any[] }) {
  const memo = sections || {}
  const evidence = Array.isArray(memo?.evidence) ? memo.evidence : []
  const risks = Array.isArray(memo?.risks) ? memo.risks : []
  const changes = Array.isArray(memo?.what_would_change_our_mind) ? memo.what_would_change_our_mind : []
  const probabilityInterpretation = memo?.probability_interpretation || null
  const probabilityBullets = Array.isArray(probabilityInterpretation?.bullets) ? probabilityInterpretation.bullets : []
  const watchItems = Array.isArray(memo?.watch_items) ? memo.watch_items : []
  const detailPanels = [
    evidence.length ? { title: 'Evidence notes', meta: `${evidence.length} notes`, body: <WorkspaceMemoList items={evidence} /> } : null,
    probabilityInterpretation ? {
      title: probabilityInterpretation?.title || 'How to read probabilities',
      meta: 'method note',
      body: <><p>{probabilityInterpretation?.summary || 'Probabilities estimate whether each public signal appears by the horizon; they are not evidence-quality scores.'}</p><WorkspaceMemoList items={probabilityBullets.slice(0, 3)} /></>,
    } : null,
    (watchItems.length || changes.length) ? { title: 'Next watchpoints', meta: `${(watchItems.length ? watchItems : changes).length} items`, body: <WorkspaceMemoList items={watchItems.length ? watchItems : changes} /> } : null,
  ].filter(Boolean)
  const secondTier = Array.isArray(memo?.second_tier_reads) && memo.second_tier_reads.length
    ? memo.second_tier_reads
    : (objects || []).slice(1, 5)
  const why = String(memo?.why_this_leads || '').trim()
  const hasMemo = why || evidence.length || risks.length || changes.length || watchItems.length || secondTier.length
  if (!hasMemo) return null
  return (
    <div className="workspace-memo-grid">
      <div className="workspace-memo-card primary">
        <span>Why this leads</span>
        <p>{why || 'The leading read is based on the highest source-backed forecast balance in the workspace.'}</p>
      </div>
      <div className="workspace-memo-card">
        <span>Second-tier reads</span>
        <div className="workspace-second-tier-list">
          {secondTier.slice(0, 4).map((row: any, i: number) => (
            <div key={row?.object_id || row?.ticker || i} className="workspace-second-tier-row">
              <div>
                <strong>{row?.label || row?.ticker || 'Object of judgment'}</strong>
                <small>{objectBenefitText(row)}</small>
              </div>
              <em>{fmtSignalScore(row?.net_score)}</em>
            </div>
          ))}
          {!secondTier.length ? <div className="muted">No second-tier reads available yet.</div> : null}
        </div>
      </div>
      {risks.length || changes.length ? (
        <div className="workspace-memo-card">
          <span>What would weaken it</span>
          <WorkspaceMemoList items={risks.length ? risks : changes} />
        </div>
      ) : null}
      {detailPanels.length ? (
        <div className="workspace-memo-detail-row">
          {detailPanels.map((panel: any) => (
            <WorkspaceDisclosureSegment key={panel.title} title={panel.title} meta={panel.meta}>
              {panel.body}
            </WorkspaceDisclosureSegment>
          ))}
        </div>
      ) : null}
    </div>
  )
}

type WorkspaceFrom = { kind: 'workspace'; id: string; title: string; path: string }

function WorkspaceCockpit({
  cockpit,
  itemBySetItemId,
  workspaceFrom,
  companyIntelRows,
}: {
  cockpit: any
  itemBySetItemId: Map<string, any>
  workspaceFrom?: WorkspaceFrom
  companyIntelRows?: any[]
}) {
  const brief = cockpit?.answer_brief || {}
  const [openObjects, setOpenObjects] = useState<Set<string>>(new Set())
  const toggleObject = (k: string) => setOpenObjects((prev) => {
    const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next
  })
  const objects = Array.isArray(cockpit?.objects) ? cockpit.objects : []
  const directEventRanking = objects.length > 0 && objects.every((object: any) => object?.score_semantic_type === 'event_probability')
  const drivers = Array.isArray(cockpit?.drivers) ? cockpit.drivers : []
  const confirmers = Array.isArray(cockpit?.confirming_tests) ? cockpit.confirming_tests : []
  const falsifiers = Array.isArray(cockpit?.falsifying_tests) ? cockpit.falsifying_tests : []
  const sourceCoverage = cockpit?.source_coverage || {}
  const evidenceDiagnostics = cockpit?.evidence_diagnostics || {}
  const monitors = Array.isArray(cockpit?.monitor_readiness?.targets) ? cockpit.monitor_readiness.targets : []
  const companyRows = Array.isArray(companyIntelRows) ? companyIntelRows : []
  const companyObjects = objects.filter((object: any) => resolveCompanyIntel(object, companyRows))
  // Rank ALL entities in one table (with predictions); company briefs are a
  // linked deep-dive below, not a parallel ranked list. (Previously entities
  // with a company brief were pulled OUT of the ranked table, leaving it with
  // only the brief-less names — a confusing "5 briefs / 1 ranked" split.)
  const objectCards = objects

  return (
    <div className="workspace-cockpit">
      <WorkspaceAtAGlance brief={brief} />

      {objectCards.length ? (
        <div className="workspace-rank-table workspace-objrank">
          <div className="workspace-rank-head workspace-objrank-row">
            <span>#</span><span>Company</span><span>Read</span><span>{directEventRanking ? 'Probability' : 'Score'}</span>
            <span>Strongest signal</span><span aria-hidden> </span>
          </div>
          {objectCards.slice(0, 12).map((object: any, i: number) => {
            const key = String(object?.object_id || object?.ticker || i)
            const confirmers = (object?.leading_confirmers || []).filter((t: any) => typeof t?.probability === 'number').sort((a: any, b: any) => b.probability - a.probability)
            const top = confirmers[0]
            const open = openObjects.has(key)
            const intel = resolveCompanyIntel(object, companyRows)
            const briefId = intel?.company_id || object?.company_id
            return (
              <React.Fragment key={key}>
                <button type="button" className="workspace-rank-row workspace-objrank-row" onClick={() => toggleObject(key)} aria-expanded={open}>
                  {/* Number by display position — the stored layer-2 rank can
                      disagree with the score-sorted order shown here, which
                      read as 3,1,6,2,... with no explanation. */}
                  <span className="workspace-rank-num">{i + 1}</span>
                  <span className="workspace-rank-name">
                    <strong>{object?.label || object?.ticker || 'Object of judgment'}</strong>
                    {object?.ticker ? <small className="muted">{object.ticker}</small> : null}
                  </span>
                  <span className={readLabelClass(object?.read_label)} style={{ justifySelf: 'start' }}>{statusLabel(object?.read_label)}</span>
                  <span className="workspace-rank-score">{fmtRankedValue(object?.net_score, object?.score_semantic_type)}</span>
                  <span>{top ? <ProbBar prob={top.probability} /> : <span className="muted" style={{ fontSize: 12 }}>—</span>}</span>
                  <span className="workspace-rank-caret" aria-hidden>{open ? '▾' : '▸'}</span>
                </button>
                {open ? (
                  <div className="workspace-rank-detail">
                    {object?.read_summary ? <p className="muted" style={{ marginTop: 0 }}>{object.read_summary}</p> : null}
                    {briefId ? <Link className="workspace-company-intel-cta" to={`/universe/companies/${briefId}`} state={workspaceFrom ? { from: workspaceFrom } : undefined}>Open company brief →</Link> : null}
                    <div className="workspace-ref-list">
                      {(object?.leading_confirmers || []).slice(0, 4).map((test: any, idx: number) => <WorkspaceCockpitTestRow key={`c-${test?.id || idx}`} test={test} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />)}
                      {(object?.leading_falsifiers || []).slice(0, 2).map((test: any, idx: number) => <WorkspaceCockpitTestRow key={`f-${test?.id || idx}`} test={test} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />)}
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            )
          })}
        </div>
      ) : null}

      <WorkspaceMemoBrief sections={brief?.memo_sections} objects={objects} />

      <WorkspaceDisclosureSegment
        title="Method and evidence quality"
        subtitle="How to interpret the cockpit and source-backed forecast base."
        meta={`${sourceCoverage?.tests_with_attached_sources || 0}/${sourceCoverage?.forecast_question_count || 0} sourced`}
      >
        <div className="workspace-cockpit-columns compact">
          <div className="workspace-cockpit-card">
            <span>How to use this</span>
            <strong>{brief?.signal_use_label || evidenceDiagnostics?.use_label || 'Signal map'}</strong>
            <small>{brief?.signal_use || evidenceDiagnostics?.use_text || 'Use this as a source-backed monitor map, not as a certainty band.'}</small>
          </div>
          <div className="workspace-cockpit-card">
            <span>Source coverage</span>
            <strong>{cockpitSourceLabel(sourceCoverage?.coverage_label)}</strong>
            <small>
              {sourceCoverage?.tests_with_attached_sources || 0}/{sourceCoverage?.forecast_question_count || 0} forecast questions with attached sources
              {/* "Partial sources" headline + "30/30 attached" read as a contradiction — say why it's partial. */}
              {Number(sourceCoverage?.verified_url_ratio ?? 1) < 1 ? ' · link verification incomplete' : ''}
              {evidenceDiagnostics?.agent_agreement_label ? ` · ${statusLabel(evidenceDiagnostics.agent_agreement_label)}` : ''}
            </small>
          </div>
        </div>
        {Array.isArray(cockpit?.quality_notes) && cockpit.quality_notes.length > 0 ? (
          <div className="workspace-cockpit-card" style={{ marginTop: 10 }}>
            <span>Quality notes and scope</span>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {cockpit.quality_notes.map((note: string, i: number) => <li key={i} style={{ marginBottom: 4 }}><small>{note}</small></li>)}
            </ul>
          </div>
        ) : null}
      </WorkspaceDisclosureSegment>

      <WorkspaceDisclosureSegment
        title="Company briefs"
        subtitle="Deep-dive read on each ranked company — proof points, risk signals and signal checks."
        meta={`${companyObjects.length} ${companyObjects.length === 1 ? 'brief' : 'briefs'}`}
      >
        <WorkspaceCompanyIntelligenceSection objects={objects} companyIntelRows={companyRows} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />
      </WorkspaceDisclosureSegment>

      <WorkspaceDisclosureSegment
        title="Signal receipts"
        subtitle="Open the audit trail behind the read. Kept collapsed so the memo and company intelligence stay primary."
        meta={`${confirmers.length + falsifiers.length} checks`}
      >
        <div className="workspace-cockpit-columns">
          <div className="workspace-cockpit-card">
            <h3>Clearest Signals</h3>
            <div className="workspace-cockpit-test-list">
              {confirmers.slice(0, 5).map((test: any, i: number) => <WorkspaceCockpitTestRow key={test?.id || i} test={test} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />)}
              {!confirmers.length ? <div className="muted">No supporting signal checks have completed yet.</div> : null}
            </div>
          </div>
          <div className="workspace-cockpit-card">
            <h3>What Would Weaken This</h3>
            <div className="workspace-cockpit-test-list">
              {falsifiers.slice(0, 5).map((test: any, i: number) => <WorkspaceCockpitTestRow key={test?.id || i} test={test} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} />)}
              {!falsifiers.length ? <div className="muted">No explicit weakening-condition tests were detected in the current signal map.</div> : null}
            </div>
          </div>
        </div>
      </WorkspaceDisclosureSegment>

      <WorkspaceDisclosureSegment
        title="Drivers and monitoring setup"
        subtitle="Operational details for users who want to inspect why these signals are being watched."
        meta={`${drivers.length} drivers · ${monitors.length} monitors`}
      >
        <div className="workspace-cockpit-columns compact">
          <div className="workspace-cockpit-card">
            <h3>What Matters</h3>
            <div className="workspace-driver-list">
              {drivers.slice(0, 6).map((driver: any, i: number) => (
                <div key={driver?.driver_id || i} className="workspace-driver">
                  <strong>{driver?.label || 'Driver'}</strong>
                  <div className="row" style={{ gap: 6, marginTop: 7 }}>
                    <span className={driver?.stance === 'falsifier' ? 'pill warn' : 'pill ok'}>{driver?.stance === 'falsifier' ? 'would weaken' : 'would support'}</span>
                    <span className="pill">priority score {fmtSignalScore(driver?.priority_score)}</span>
                    <span className="pill">{driver?.object_count || 0} objects</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="workspace-cockpit-card">
            <h3>Monitors</h3>
            <div className="workspace-question-list">
              {monitors.slice(0, 6).map((monitor: any, i: number) => (
                <div key={monitor?.monitor_id || monitor?.set_item_id || i} className="workspace-question-row">
                  <span className={cockpit?.monitor_readiness?.status === 'active' ? 'pill ok' : 'pill'}>{statusLabel(cockpit?.monitor_readiness?.status)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 750 }}>{monitor?.label || monitor?.object_label || 'Monitor'}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{monitor?.monitor_rule || 'Refresh when public evidence changes the forecast question.'}</div>
                  </div>
                </div>
              ))}
              {!monitors.length ? <div className="muted">No monitor rules are configured yet.</div> : null}
            </div>
          </div>
        </div>
      </WorkspaceDisclosureSegment>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lightweight per-workspace personalization. There is no server endpoint to
// persist UI preferences yet, so we keep the user's thesis note and Read-tab
// layout (section order + hidden sections) in localStorage, keyed by the
// workspace id. This is a clean upgrade path to server persistence later.
// ---------------------------------------------------------------------------
type WorkspaceLayout = { order: string[]; hidden: string[] }
const EMPTY_LAYOUT: WorkspaceLayout = { order: [], hidden: [] }

function noteStorageKey(id?: string) { return `cenva:ws:note:${id || ''}` }
function layoutStorageKey(id?: string) { return `cenva:ws:layout:${id || ''}` }

function loadNote(id?: string): string {
  try { return window.localStorage.getItem(noteStorageKey(id)) || '' } catch { return '' }
}
function loadLayout(id?: string): WorkspaceLayout {
  try {
    const raw = window.localStorage.getItem(layoutStorageKey(id))
    if (!raw) return { ...EMPTY_LAYOUT }
    const parsed = JSON.parse(raw)
    return {
      order: Array.isArray(parsed?.order) ? parsed.order.map(String) : [],
      hidden: Array.isArray(parsed?.hidden) ? parsed.hidden.map(String) : [],
    }
  } catch { return { ...EMPTY_LAYOUT } }
}

// Sort section descriptors by the user's saved order; sections not in the
// saved order keep their natural position after the ordered ones.
function applyLayoutOrder<T extends { key: string }>(defs: T[], order: string[]): T[] {
  const byKey = new Map(defs.map((d) => [d.key, d]))
  const ordered: T[] = []
  order.forEach((k) => { const d = byKey.get(k); if (d) { ordered.push(d); byKey.delete(k) } })
  defs.forEach((d) => { if (byKey.has(d.key)) ordered.push(d) })
  return ordered
}

// Segmented control that switches the workspace detail between the calm daily
// "Read" and the "Build & Method" workflow/provenance surface.
function WorkspaceViewTabs({
  view,
  onChange,
  buildNeedsAttention,
}: {
  view: 'read' | 'build'
  onChange: (v: 'read' | 'build') => void
  buildNeedsAttention?: boolean
}) {
  return (
    <div className="workspace-view-tabs" role="tablist" aria-label="Workspace view">
      <button
        role="tab"
        aria-selected={view === 'read'}
        className={view === 'read' ? 'workspace-view-tab active' : 'workspace-view-tab'}
        onClick={() => onChange('read')}
      >
        The Read
      </button>
      <button
        role="tab"
        aria-selected={view === 'build'}
        className={view === 'build' ? 'workspace-view-tab active' : 'workspace-view-tab'}
        onClick={() => onChange('build')}
      >
        Build &amp; Method
        {buildNeedsAttention ? <span className="workspace-view-dot" aria-label="Action needed" /> : null}
      </button>
    </div>
  )
}

export default function WorkspaceDetail() {
  const { workspaceId } = useParams()
  // "I'm feeling lucky": ?auto=1 means the user asked Cenva to take the
  // shortlist and run forecasts without manual review. We auto-approve the
  // shortlist and start the forecast run once each (guarded by refs).
  const [searchParams] = useSearchParams()
  const autoBuild = searchParams.get('auto') === '1'
  const autoSavedRef = useRef(false)
  const autoRanRef = useRef(false)
  // Active-org context (cached; Topbar issues the same query). Batch pipeline
  // execution is API-gated to the Sigil Labs org, so the batch control only
  // renders there — every other org builds via the live path.
  const meQuery = useQuery<MeResp>({ queryKey: qk.me(), queryFn: () => clientApi.me(), staleTime: 30_000 })
  const isBatchPipelineOrg = String(meQuery.data?.active_org?.slug || '') === 'sigil-labs-inc'
  const [data, setData] = useState<WorkspaceDetailType | null>(null)
  const [workspaceGraph, setWorkspaceGraph] = useState<any | null>(null)
  const [error, setError] = useState<any>(null)
  const [executing, setExecuting] = useState(false)
  const [planning, setPlanning] = useState(false)
  const [scoping, setScoping] = useState(false)
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null)
  const [reviewingShortlist, setReviewingShortlist] = useState(false)
  const [expandedRanked, setExpandedRanked] = useState<Set<string>>(new Set())
  const [shortlistKeys, setShortlistKeys] = useState<string[]>([])
  const [execution, setExecution] = useState<WorkspaceExecuteResp | null>(null)
  // Live + Sonnet 5 + enqueue is the one execution combination the API allows
  // for every org; batch/pipeline/model overrides are Sigil-Labs-only. The
  // old defaults (batch, no forecasts) made the primary build CTA 403 for
  // every client org.
  const [enqueueForecasts, setEnqueueForecasts] = useState(true)
  const [executionMode, setExecutionMode] = useState<'live' | 'batch'>('live')
  const [forecastModel] = useState<'claude-sonnet-5' | 'claude-opus-4-6'>('claude-sonnet-5')
  // Empty default so the highlighted/inspected build step follows the actual
  // running step (first not-done) instead of always showing "Running forecasts"
  // — which made the rail highlight forecasts while the workspace was still
  // only at the scoping stage. The user can still click any step to inspect it.
  const [selectedBuildStep, setSelectedBuildStep] = useState('')
  const [manualCandidateText, setManualCandidateText] = useState('')
  const [manualCandidates, setManualCandidates] = useState<any[]>([])
  const [discoveryAnswerValue, setDiscoveryAnswerValue] = useState('')
  // Read vs Build & Method, and lightweight per-workspace personalization.
  const [view, setView] = useState<'read' | 'build'>('read')
  const [customizing, setCustomizing] = useState(false)
  const [note, setNote] = useState('')
  const [layout, setLayout] = useState<WorkspaceLayout>(EMPTY_LAYOUT)
  const viewInitialized = useRef<string | null>(null)

  // Load persisted note + layout when the workspace changes.
  useEffect(() => {
    setNote(loadNote(workspaceId))
    setLayout(loadLayout(workspaceId))
    setCustomizing(false)
    viewInitialized.current = null
  }, [workspaceId])

  function persistNote(value: string) {
    setNote(value)
    try { window.localStorage.setItem(noteStorageKey(workspaceId), value) } catch { /* ignore */ }
  }
  function persistLayout(next: WorkspaceLayout) {
    setLayout(next)
    try { window.localStorage.setItem(layoutStorageKey(workspaceId), JSON.stringify(next)) } catch { /* ignore */ }
  }
  function toggleSectionHidden(key: string) {
    const hidden = layout.hidden.includes(key)
      ? layout.hidden.filter((k) => k !== key)
      : [...layout.hidden, key]
    persistLayout({ ...layout, hidden })
  }
  // Move a section up/down within the supplied ordered key list.
  function moveSection(orderedKeys: string[], key: string, dir: -1 | 1) {
    const idx = orderedKeys.indexOf(key)
    const target = idx + dir
    if (idx < 0 || target < 0 || target >= orderedKeys.length) return
    const next = [...orderedKeys]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    persistLayout({ ...layout, order: next })
  }
  function resetLayout() {
    persistLayout({ ...EMPTY_LAYOUT })
  }

  async function refreshWorkspaceGraph() {
    if (!workspaceId) return
    try {
      const graph = await clientApi.workspaceGraph(workspaceId)
      setWorkspaceGraph(graph.graph)
    } catch {
      setWorkspaceGraph(null)
    }
  }

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    setWorkspaceGraph(null)
    clientApi.workspaceDetail(workspaceId)
      .then((res) => { if (!cancelled) setData(res) })
      .catch((e) => { if (!cancelled) setError(e) })
    clientApi.workspaceGraph(workspaceId)
      .then((res) => { if (!cancelled) setWorkspaceGraph(res.graph) })
      .catch(() => { if (!cancelled) setWorkspaceGraph(null) })
    return () => { cancelled = true }
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId || !workspaceIsGenerating(data)) return
    let cancelled = false
    const id = window.setInterval(() => {
      clientApi.workspaceDetail(workspaceId)
        .then((res) => { if (!cancelled) setData(res) })
        .catch(() => null)
    }, 4000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [workspaceId, data?.status, data?.question_set?.updated_at])

  useEffect(() => {
    const opts = Array.isArray(data?.discovery?.next_question?.options) ? data.discovery.next_question.options : []
    setDiscoveryAnswerValue(opts.length ? String(opts[0]) : '')
  }, [workspaceId, data?.discovery?.next_question?.id])

  useEffect(() => {
    const layer0 = data?.question_set?.summary_json?.workspace_layer0
    if (!layer0?.candidate_map?.length) return
    const reviewedCandidates = Array.isArray(layer0?.shortlist_review?.selected_candidates) ? layer0.shortlist_review.selected_candidates : []
    const reviewedTickers = Array.isArray(layer0?.shortlist_review?.selected_tickers) ? layer0.shortlist_review.selected_tickers : []
    const current = reviewedCandidates.length
      ? reviewedCandidates.map((c: any, i: number) => candidateKey(c, i))
      : reviewedTickers.length
        ? reviewedTickers.map((ticker: any) => `ticker:${String(ticker).toUpperCase()}`)
        : (Array.isArray(layer0?.top_10_shortlist) ? layer0.top_10_shortlist.map((c: any, i: number) => candidateKey(c, i)) : [])
    setShortlistKeys(current.slice(0, 10))
  }, [workspaceId, data?.question_set?.summary_json?.workspace_layer0?.generated_at, data?.question_set?.summary_json?.workspace_layer0?.shortlist_review?.reviewed_at])

  // "I'm feeling lucky" auto-flow: when ?auto=1, take the auto-shortlist and
  // run forecasts without manual review. Flags are recomputed from `data` here
  // (rather than the later render-scope consts) to avoid a temporal-dead-zone
  // reference; the two steps each fire once via refs.
  useEffect(() => {
    if (!autoBuild || !data || !workspaceId) return
    const summary = data?.question_set?.summary_json || {}
    const layer0 = summary?.workspace_layer0 || {}
    const layer3 = summary?.workspace_layer3 || {}
    const exec = summary?.workspace_execution || {}
    const cmap = Array.isArray(layer0?.candidate_map) ? layer0.candidate_map : []
    const hasItems = (data?.items?.length || 0) > 0
    const reviewed = layer0?.shortlist_review?.status === 'user_reviewed'
    const oppResearch = String(layer0?.research_branch?.mode || '') === 'opportunity_space_research'
    const layer0Ready = layer0?.status === 'scoping_ready'
    const needsReview = cmap.length > 0 && !hasItems && !reviewed && !oppResearch
    // Step 1 — auto-approve the shortlist once it's ready.
    if (layer0Ready && needsReview && !reviewingShortlist && !autoSavedRef.current && shortlistKeys.length > 0) {
      autoSavedRef.current = true
      saveShortlist()
      return
    }
    // Step 2 — once approved, kick off the forecast run once.
    const execStarted = hasItems || ['layer_1_ready', 'materialized', 'layer_1_batch_ready', 'batch_submitted', 'forecasting', 'forecasted', 'layer_2_ready'].includes(String(exec?.status || '').toLowerCase())
    if (layer0Ready && !needsReview && !layer3?.client_answer && !execStarted && !executing && !autoRanRef.current) {
      autoRanRef.current = true
      ;(isBatchPipelineOrg ? runBatchPipeline : materializeWorkspace)()
    }
  }, [autoBuild, data, workspaceId, executing, reviewingShortlist, isBatchPipelineOrg, shortlistKeys])

  // Pick the initial tab once per workspace: if there's already a current read,
  // open The Read; otherwise open Build & Method where the build controls live.
  // A ref guards this so it never overrides a manual tab switch.
  useEffect(() => {
    if (!data || !workspaceId || viewInitialized.current === workspaceId) return
    viewInitialized.current = workspaceId
    const hasAnswer = Boolean(data?.question_set?.summary_json?.workspace_layer3?.client_answer)
    setView(hasAnswer ? 'read' : 'build')
  }, [data, workspaceId])

  async function refreshWorkspaceNow() {
    if (!workspaceId || refreshJobId) return
    setError(null)
    try {
      const res = await clientApi.refreshWorkspace(workspaceId)
      // refresh_in_progress returns the job already running — poll that one.
      if (res.job_id) setRefreshJobId(res.job_id)
    } catch (e) {
      setError(e)
    }
  }

  // While a manual refresh job is in flight, poll it; when it lands, reload
  // the workspace so the new read/data appears without a page refresh.
  useEffect(() => {
    if (!workspaceId || !refreshJobId) return
    let cancelled = false
    const id = window.setInterval(async () => {
      try {
        const job = await clientApi.jobStatus(refreshJobId)
        const status = String(job?.status || '').toLowerCase()
        if (cancelled || status === 'queued' || status === 'running') return
        setRefreshJobId(null)
        const refreshed = await clientApi.workspaceDetail(workspaceId)
        if (!cancelled) setData(refreshed)
        await refreshWorkspaceGraph()
      } catch {
        // transient poll failure: keep polling
      }
    }, 5000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [workspaceId, refreshJobId])

  async function materializeWorkspace() {
    if (!workspaceId) return
    setExecuting(true)
    setError(null)
    try {
      const res = await clientApi.executeWorkspace(workspaceId, {
        enqueue_forecasts: enqueueForecasts,
        execution_mode: executionMode,
        model: forecastModel,
      })
      setExecution(res)
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
    } catch (e) {
      setError(e)
    } finally {
      setExecuting(false)
    }
  }

  async function runBatchPipeline() {
    if (!workspaceId) return
    setExecuting(true)
    setError(null)
    try {
      const res = await clientApi.executeWorkspace(workspaceId, {
        pipeline: true,
        execution_mode: 'batch',
        model: forecastModel,
        budget_profile: 'workspace_balanced_thinking',
      })
      setExecution(res)
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
    } catch (e) {
      setError(e)
    } finally {
      setExecuting(false)
    }
  }

  async function activateMonitoring() {
    if (!workspaceId) return
    setExecuting(true)
    setError(null)
    try {
      await clientApi.activateWorkspace(workspaceId)
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
      setExecution(null)
    } catch (e) {
      setError(e)
    } finally {
      setExecuting(false)
    }
  }

  async function compileDiscoveryPlan() {
    if (!workspaceId) return
    setPlanning(true)
    setError(null)
    try {
      await clientApi.planWorkspaceDiscovery(workspaceId)
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
    } catch (e) {
      setError(e)
    } finally {
      setPlanning(false)
    }
  }

  async function answerDiscoveryQuestion() {
    const qid = data?.discovery?.next_question?.id
    if (!workspaceId || !qid || !discoveryAnswerValue.trim()) return
    setPlanning(true)
    setError(null)
    try {
      await clientApi.answerWorkspaceDiscovery(workspaceId, { answers: { [String(qid)]: discoveryAnswerValue } })
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
    } catch (e) {
      setError(e)
    } finally {
      setPlanning(false)
    }
  }

  async function runLayer0Scoping() {
    if (!workspaceId) return
    setScoping(true)
    setError(null)
    try {
      await clientApi.scopeWorkspace(workspaceId)
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
    } catch (e) {
      setError(e)
    } finally {
      setScoping(false)
    }
  }

  async function saveShortlist() {
    if (!workspaceId) return
    setReviewingShortlist(true)
    setError(null)
    try {
      const candidateMap = Array.isArray(data?.question_set?.summary_json?.workspace_layer0?.candidate_map) ? data.question_set.summary_json.workspace_layer0.candidate_map : []
      const candidates = shortlistKeys
        .map((key) => candidateMap.find((candidate: any, i: number) => candidateKey(candidate, i) === key) || manualCandidates.find((candidate: any, i: number) => candidateKey(candidate, i) === key))
        .filter(Boolean)
      await clientApi.reviewWorkspaceShortlist(workspaceId, { candidates })
      const refreshed = await clientApi.workspaceDetail(workspaceId)
      setData(refreshed)
      await refreshWorkspaceGraph()
    } catch (e) {
      setError(e)
    } finally {
      setReviewingShortlist(false)
    }
  }

  function toggleShortlistCandidate(candidate: any, index: number) {
    const key = candidateKey(candidate, index)
    if (!key) return
    setShortlistKeys((prev) => {
      if (prev.includes(key)) return prev.filter((t) => t !== key)
      if (prev.length >= 10) return prev
      return [...prev, key]
    })
  }

  function addManualCandidate() {
    const text = manualCandidateText.trim()
    if (!text) return
    const tickerMatch = text.match(/^([A-Z0-9.]{1,10})\s+(.+)$/)
    const ticker = tickerMatch ? tickerMatch[1].toUpperCase() : ''
    const name = tickerMatch ? tickerMatch[2].trim() : text
    const row = {
      ticker,
      name,
      role: 'user_added_candidate',
      source: 'user_added',
      underrecognized_mechanism: 'User-added candidate for workspace review.',
      provisional_score: 70,
      route_type: ticker ? 'direct_public_security' : 'manual_candidate_needs_access_verification',
      listing_status: ticker ? 'user_provided_ticker_pending_verification' : 'needs_access_verification',
    }
    const key = candidateKey(row, manualCandidates.length)
    setManualCandidates((prev) => [...prev, row])
    setShortlistKeys((prev) => prev.includes(key) || prev.length >= 10 ? prev : [...prev, key])
    setManualCandidateText('')
  }

  if (!data && !error) return (
    <div className="container">
      <Topbar subtitle="Forecast Workspace" />
      <div className="grid" style={{ marginTop: 16 }}><Skeleton variant="card" count={3} /></div>
    </div>
  )

  const draft = data?.draft
  const hasDraft = Boolean(draft?.title)
  const discovery = data?.discovery
  const summary = data?.question_set?.summary_json || {}
  const companyIntelRows = workspaceCompanyIntelRows(summary)
  const layer0 = summary?.workspace_layer0
  const preparation = summary?.workspace_layer1_preparation
  const workspaceExecution = summary?.workspace_execution || {}
  const layer2 = summary?.workspace_layer2
  const layer3 = summary?.workspace_layer3
  const publicationReview = summary?.workspace_readiness_review || summary?.workspace_publication_review
  const lifecycle = summary?.workspace_lifecycle
  const layer0Ready = layer0?.status === 'scoping_ready'
  const candidateMap = Array.isArray(layer0?.candidate_map) ? layer0.candidate_map : []
  const reviewCandidates = [...candidateMap, ...manualCandidates]
  const shortlistReview = layer0?.shortlist_review || {}
  const readinessScore = layer0?.workspace_readiness_score || {}
  const signalObjects = Array.isArray(layer0?.signal_map?.objects) ? layer0.signal_map.objects : []
  const shortlistReviewed = shortlistReview?.status === 'user_reviewed'
  const hasItems = (data?.items?.length || 0) > 0
  const candidateDiscoveryStatus = String(layer0?.candidate_discovery?.status || '')
  const opportunitySpaceResearch = String(layer0?.research_branch?.mode || '') === 'opportunity_space_research'
  const intentMode = String(draft?.workspace_intent?.entity_mode || '')
  const answerType = String(layer3?.client_answer?.answer_type || '')
  const isMonitorWorkspace = candidateDiscoveryStatus === 'not_applicable_non_entity_workspace' || answerType === 'monitor_prioritization' || ['indicator_dashboard', 'scenario_monitor'].includes(intentMode)
  const requiresCandidateReview = candidateMap.length > 0 && !hasItems && !shortlistReviewed && candidateDiscoveryStatus !== 'seed_scope_locked' && !opportunitySpaceResearch
  const activeMonitoring = lifecycle?.status === 'active_monitoring'
  const readinessReady = publicationReview?.ready_for_internal_use === true || publicationReview?.publishable === true
  const externalReady = publicationReview?.external_distribution_ready === true
  const rankedEntities = layer2?.opportunity_map?.ranked_entities || []
  const topEntities = layer3?.client_answer?.top_entities || rankedEntities.slice(0, 5)
  const keyTakeaways = Array.isArray(layer3?.client_answer?.key_takeaways) ? layer3.client_answer.key_takeaways : []
  const watchItems = Array.isArray(layer3?.client_answer?.watch_items) ? layer3.client_answer.watch_items : []
  const dataBasis = layer3?.client_answer?.data_basis || {}
  const rankedForecastQuestions = rankedForecastQuestionsFrom(layer2, layer3)
  const citationRefs = Array.isArray(layer3?.client_answer?.citation_refs) ? layer3.client_answer.citation_refs : []
  const thesisCards = layer2?.entity_thesis_cards || []
  const scoreFormula = dataBasis?.score_formula || layer2?.opportunity_map?.score_formula
  const lifecycleMonitors = lifecycle?.monitor_targets || []
  const monitorPreview = lifecycleMonitors.slice(0, 8)
  const primitiveItems = (data?.items || []).filter((item: any) => item?.question_spec_json?.workspace_layer === 'layer_1')
  const itemBySetItemId = new Map(primitiveItems.map((item: any) => [String(item?.set_item_id || ''), item]).filter(([key]) => key))
  const sourceRecords = Array.isArray(data?.sources) ? data.sources : []
  const agentSourceLabels = agentSourceLabelCount(primitiveItems)
  const verifiedSourceRecords = preparation?.verified_source_record_count ?? preparation?.verified_source_count ?? dataBasis.verified_source_count ?? sourceRecords.length ?? 0
  const socialSignalsByTicker = summary?.workspace_social_signals && typeof summary.workspace_social_signals === 'object' ? summary.workspace_social_signals : {}
  const earlySignals = (topEntities || []).flatMap((entity: any) => {
    const ticker = String(entity?.ticker || '').toUpperCase()
    const rows = Array.isArray(socialSignalsByTicker?.[ticker]) ? socialSignalsByTicker[ticker] : []
    return rows.map((signal: any) => ({ ...signal, ticker }))
  })
  const entityEvidenceRefs = (topEntities || []).flatMap((entity: any) => {
    const thesis = thesisCards.find((t: any) => String(t?.ticker || '') === String(entity?.ticker || '')) || {}
    const refs = Array.isArray(entity?.primitive_refs) && entity.primitive_refs.length
      ? entity.primitive_refs
      : Array.isArray(thesis?.primitive_refs) ? thesis.primitive_refs : []
    return refs.map((ref: any) => ({ ...ref, ticker: entity?.ticker, entity_name: entity?.entity_name }))
  })
  const monitorEvidenceRefs = rankedForecastQuestions.length ? rankedForecastQuestions : citationRefs
  const evidenceRefs = isMonitorWorkspace && monitorEvidenceRefs.length ? monitorEvidenceRefs : entityEvidenceRefs
  const fallbackEvidenceRefs = evidenceRefs.length ? evidenceRefs : primitiveItems.map((item: any) => {
    const spec = item?.question_spec_json || {}
    const output = item?.forecast_output_json || {}
    return {
      ticker: spec?.entity?.ticker,
      entity_name: spec?.entity?.name,
      driver: spec?.driver,
      question_text: spec?.question_text || spec?.title,
      probability: output?.probability,
      confidence: output?.confidence,
      run_id: output?.run_id || item?.run_id,
      question_id: item?.question_id,
      set_item_id: item?.set_item_id,
    }
  })
  // A workspace with a synthesized read is never "Building" — a flagged read
  // is "Needs attention" (a finished dividend workspace showed "BUILDING
  // WORKSPACE" over a complete current read).
  const hasRead = Boolean(layer3?.client_answer)
  const heroKicker = activeMonitoring
    ? 'Live workspace'
    : externalReady
      ? 'Ready to share'
      : readinessReady
        ? 'Ready'
        : hasRead
          ? 'Needs attention'
          : hasItems
            ? 'Building workspace'
            : 'Draft workspace'
  const isGenerating = workspaceIsGenerating(data)
  // Async layer-0 scoping marker — server-side truth, so the "preparing
  // signal map" panel survives page reloads and isn't tied to one request.
  const scopingState = summary?.workspace_layer0_scoping && typeof summary.workspace_layer0_scoping === 'object' ? summary.workspace_layer0_scoping : null
  const scopingInFlight = ['queued', 'running'].includes(String(scopingState?.status || '').toLowerCase())
  const scopingFailedError = String(scopingState?.status || '').toLowerCase() === 'failed' ? String(scopingState?.error || 'Signal map preparation failed. Try again.') : null
  const forecastCount = data?.question_set?.forecasted_count || 0
  const totalChecks = data?.question_set?.generated_count || primitiveItems.length || data?.items?.length || 0
  const canActivateMonitoring = workspaceCanActivateMonitoring(data)
  const workspaceDisplay = summary?.workspace_display && typeof summary.workspace_display === 'object' ? summary.workspace_display : {}
  const displayTitle = workspaceDisplay?.title || compactWorkspaceTitle(draft?.title, draft?.original_question, layer3?.client_answer?.headline)
  const displaySubtitle = workspaceDisplay?.subtitle || layer3?.client_answer?.headline || draft?.original_question
  const topSignal = rankedForecastQuestions[0]
  const strongSignalCount = rankedForecastQuestions.filter((ref: any) => Number(ref?.probability) >= 0.6).length
  const topFalsifier = rankedForecastQuestions.find((ref: any) => looksLikeFalsifier(ref))
  const monitorState = activeMonitoring ? 'Live' : canActivateMonitoring ? 'Ready to activate' : lifecycle?.active_monitor_count ? 'Partially active' : 'Not active'
  const sourceState = verifiedSourceRecords ? 'Verified source records' : agentSourceLabels ? 'Forecast source labels' : 'Needs sources'
  const cockpitView = graphView(workspaceGraph, 'intelligence_cockpit')
  const changeLogView = graphView(workspaceGraph, 'change_log')
  const cockpit = workspaceGraph?.cockpit || cockpitView?.payload || null
  const whatChanged = summary?.workspace_what_changed && typeof summary.workspace_what_changed === 'object' ? summary.workspace_what_changed : null
  const graphChangeSummary = changeLogView?.payload || null
  const latestWorkspaceUpdateAt = freshest(
    whatChanged?.latest_update_at,
    (data as any)?.updated_at,
    (data as any)?.question_set?.updated_at,
  )
  const cockpitReady = cockpit?.status === 'ready'
  const workspaceFrom: WorkspaceFrom | undefined = workspaceId
    ? {
        kind: 'workspace',
        id: workspaceId,
        title: displayTitle || 'Workspace',
        path: `/workspaces/${workspaceId}`,
      }
    : undefined
  const topbarRight = (
    <div className="row" style={{ gap: 8 }}>
      {/* Manual refresh: works even when scheduled monitoring is off for the
          org (the switch governs automatic spend, not explicit requests). */}
      {workspaceId && forecastCount > 0 ? (
        <button className="btn" onClick={refreshWorkspaceNow} disabled={Boolean(refreshJobId)}>
          {refreshJobId ? 'Refreshing…' : 'Refresh now'}
        </button>
      ) : null}
      {workspaceId && layer3?.client_answer ? <DownloadReportButton kind="workspace" targetId={workspaceId} label="Download PDF" /> : null}
      <Link className="btn" to="/workspaces/new">New workspace</Link>
    </div>
  )

  const activeBuildAction = (scoping || scopingInFlight) ? 'scoping' : executing ? 'building' : undefined
  if (hasDraft && draft && (isGenerating || Boolean(activeBuildAction))) {
    return (
      <div className="container">
        <Topbar subtitle="Forecast Workspace" right={topbarRight} />
        {error && <ErrorBox error={error} />}
        <WorkspaceStateNotice state={(data as any)?.client_state} variant="banner" />
        <div className="workspace-hero" style={{ marginTop: 16 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>{autoBuild ? 'Assembling forecast workspace · Decide-for-me' : 'Assembling forecast workspace'}</div>
              <h1>{displayTitle}</h1>
              <p className="muted">{displaySubtitle}</p>
              {draft.original_question ? <div className="workspace-original-question"><strong>Original question</strong><span>{draft.original_question}</span></div> : null}
            </div>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
              <span className="pill ok">{draft.horizon}</span>
              {totalChecks ? <span className="pill">{forecastCount}/{totalChecks} forecasts</span> : null}
            </div>
          </div>
        </div>
        <WorkspaceBuildProgress data={data} selectedKey={selectedBuildStep} onSelect={setSelectedBuildStep} activeAction={activeBuildAction} />
      </div>
    )
  }

  // -------------------------------------------------------------------------
  // Section nodes for the Read / Build & Method split. The hero (title +
  // current read + build controls) stays above the tabs as the anchor; these
  // nodes fill the body of whichever tab is active.
  // -------------------------------------------------------------------------
  const signalMapNode = isMonitorWorkspace && signalObjects.length ? (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>Signal Map</div>
          <h2>Operating Signals Being Watched</h2>
          <p className="muted">No company shortlist is needed for this workspace. These are the capability and evidence objects that replace candidate cards.</p>
        </div>
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          <span className="pill">{signalObjects.length} signals</span>
          <span className={layer0?.signal_map?.status === 'ready_for_execution' ? 'pill ok' : 'pill'}>{statusLabel(layer0?.signal_map?.status || 'draft')}</span>
        </div>
      </div>
      <div className="workspace-question-list" style={{ marginTop: 14 }}>
        {signalObjects.slice(0, 10).map((signal: any, i: number) => (
          <div key={signal?.signal_id || i} className="workspace-question-row">
            <span className={signal?.polarity === 'negative' ? 'pill bad' : 'pill ok'}>{signal?.polarity === 'negative' ? 'weakening' : 'support'}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 800 }}>{signal?.label || signal?.title || `Signal ${i + 1}`}</div>
              {signal?.operating_meaning ? <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{signal.operating_meaning}</div> : null}
              <div className="row" style={{ gap: 6, marginTop: 8 }}>
                <span className={signal?.resolvability?.status === 'needs_repair' ? 'pill bad' : 'pill'}>{statusLabel(signal?.resolvability?.status || 'source plan')}</span>
                <span className="pill">{statusLabel(signal?.baseline_status || 'baseline pending')}</span>
                <span className="pill">{statusLabel(signal?.coverage_status || 'needs sources')}</span>
              </div>
              {Array.isArray(signal?.source_categories) && signal.source_categories.length ? (
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  {signal.source_categories.slice(0, 4).map((category: string) => <span key={category} className="pill">{category}</span>)}
                </div>
              ) : null}
              {signal?.source_plan ? <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Source plan: {signal.source_plan}</div> : null}
              {signal?.strengthens_read_if || signal?.weakens_read_if ? (
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  {signal?.strengthens_read_if ? `Strengthens if: ${signal.strengthens_read_if}` : ''}
                  {signal?.weakens_read_if ? `${signal?.strengthens_read_if ? ' ' : ''}Weakens if: ${signal.weakens_read_if}` : ''}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null

  const candidateReviewNode = layer0Ready && !hasItems && candidateMap.length ? (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>{opportunitySpaceResearch ? 'Candidate Review' : 'Step 1 of 2 · Review companies'}</div>
          <h2>{opportunitySpaceResearch ? 'Evidence leads found so far' : 'Approve the forecast universe'}</h2>
          <p className="muted">{opportunitySpaceResearch ? 'Cenva found early public-company leads, but not enough verified coverage for a defensible ranked read yet. The next step tests the opportunity itself before ranking companies.' : 'Cenva scoped the candidate companies and selected an initial top ten. Adjust the universe if you want, then save it — that unlocks Step 2, where Cenva runs the forecasts on these names.'}</p>
        </div>
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          <span className={Number(readinessScore?.score || 0) >= 70 ? 'pill ok' : 'pill'}>{readinessScore?.score ?? 0}/100 readiness</span>
          <span className="pill">{shortlistKeys.length}/10 selected</span>
          {opportunitySpaceResearch ? <span className="pill ok">review optional</span> : shortlistReviewed ? <span className="pill ok">reviewed</span> : autoBuild ? <span className="pill ok">auto-approving…</span> : <span className="pill">pending review</span>}
        </div>
      </div>
      {Array.isArray(readinessScore?.issues) && readinessScore.issues.length ? (
        <div className="workspace-question-list" style={{ marginTop: 12 }}>
          {readinessScore.issues.slice(0, 3).map((issue: string, i: number) => <div key={i} className="workspace-question-row"><span className="pill">gate</span><div className="muted">{issue}</div></div>)}
        </div>
      ) : null}
      {!opportunitySpaceResearch ? (
        <p className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
          Tap a company to add or remove it. Each row shows <strong>why it fits the thesis</strong>, the supporting source quote, and three scores —
          {' '}<strong>exposure</strong> (fundamentals fit the thesis), <strong>recognition gap</strong> (how under-appreciated vs consensus), and <strong>data</strong> (how much hard data backs it). These names become the evidence base Cenva forecasts in Step 2.
        </p>
      ) : null}
      <div className="workspace-question-list" style={{ marginTop: 14 }}>
        {reviewCandidates.slice(0, 50).map((candidate: any, i: number) => {
          const ticker = String(candidate?.ticker || '').toUpperCase()
          const key = candidateKey(candidate, i)
          const selected = shortlistKeys.includes(key)
          const scores = candidate?.scores || {}
          const resolution = candidate?.entity_resolution || {}
          const firstEvidence = Array.isArray(candidate?.discovery_evidence) ? candidate.discovery_evidence[0] : null
          const route = candidate?.route_type || candidate?.listing_status || candidate?.access_status?.route_type || candidate?.source || 'candidate'
          const allEvidence = Array.isArray(candidate?.discovery_evidence) ? candidate.discovery_evidence : []
          // Raw search snippets are often scrape boilerplate (nav chrome, error
          // banners, video metadata) rather than a usable quote. Clean the
          // markdown/HTML, then suppress the obvious junk so the card shows a
          // clean quote or nothing — the "Why it fits" rationale already
          // carries the substance.
          const cleanedSnippet = cleanSnippet(firstEvidence?.snippet || firstEvidence?.content || '', 240).replace(/^#+\s*/, '').trim()
          const snippetIsJunk = !cleanedSnippet || cleanedSnippet.length < 40 || /(oops,? something went wrong|skip to main content|enable javascript|accept (all )?cookies|sign in to|- Length:|- Views:|- Likes:|- Published:|Channel:\s)/i.test(cleanedSnippet)
          const evidenceSnippet = snippetIsJunk ? '' : cleanedSnippet
          const evidenceUrl = firstEvidence?.url || ''
          const evidenceLabel = firstEvidence?.title || firstEvidence?.domain || 'search evidence'
          const toggle = () => toggleShortlistCandidate(candidate, i)
          return (
            <div
              key={key || `${ticker || i}`}
              className="workspace-question-row"
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              style={{ textAlign: 'left', width: '100%', cursor: 'pointer', alignItems: 'flex-start' }}
              onClick={toggle}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle() } }}
            >
              <span className={selected ? 'pill ok' : 'pill'}>{selected ? '✓ selected' : `#${candidate?.rank || i + 1}`}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 800 }}>{candidate?.name || ticker} {ticker ? <span className="muted">({ticker})</span> : null}</div>
                {candidate?.underrecognized_mechanism ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{candidate.underrecognized_mechanism}</div> : null}
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  <span className="pill">{statusLabel(route)}</span>
                  <span className="pill" title="How strongly the company's fundamentals fit the workspace thesis (0–100).">exposure {scores?.fundamental_exposure ?? 'n/a'}</span>
                  <span className="pill" title="How under-appreciated this name looks versus consensus — higher means less crowded (0–100).">recognition gap {scores?.recognition_gap ?? 'n/a'}</span>
                  <span className="pill" title="How much hard data Cenva already has to forecast this name (0–100). Low means more evidence is gathered before forecasting.">data {scores?.data_availability ?? 'n/a'}</span>
                  <span className={resolution?.status === 'matched_cenva_company' ? 'pill ok' : 'pill'} title="Whether this company is already covered in Cenva's data, or treated as external.">{statusLabel(resolution?.coverage_status || 'external')}</span>
                </div>
                {candidate?.selection_reason ? (
                  <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5, color: candidate?.selection_state === 'selected' ? 'var(--ok, #16a34a)' : 'var(--muted, #94a3b8)' }}>
                    <strong>{candidate?.selection_state === 'selected' ? 'Pre-selected:' : 'Not pre-selected:'}</strong> {String(candidate.selection_reason).replace(/^(Pre-selected|Not pre-selected|Excluded)\s*—\s*/, '')}
                  </div>
                ) : null}
                {candidate?.llm_relevance?.reason ? (
                  <div style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}>
                    <strong>Why it fits:</strong> <span className="muted">{candidate.llm_relevance.reason}</span>
                  </div>
                ) : null}
                {evidenceSnippet ? (
                  <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5, paddingLeft: 10, borderLeft: '2px solid rgba(148,163,184,0.3)' }}>
                    “{evidenceSnippet}”
                  </div>
                ) : null}
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  {firstEvidence ? (
                    <>
                      Source:{' '}
                      {evidenceUrl ? (
                        <a href={evidenceUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{evidenceLabel}</a>
                      ) : (
                        <span>{evidenceLabel}</span>
                      )}
                      {allEvidence.length > 1 ? <span> · +{allEvidence.length - 1} more {allEvidence.length - 1 === 1 ? 'source' : 'sources'}</span> : null}
                    </>
                  ) : (
                    <span>No source evidence yet — Cenva will gather it before forecasting.</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 14 }}>
        <input className="input" style={{ flex: 1 }} value={manualCandidateText} onChange={(e) => setManualCandidateText(e.target.value)} placeholder="Add candidate manually, e.g. ADYEN.AS Adyen or StepFun" />
        <button className="btn" onClick={addManualCandidate} disabled={!manualCandidateText.trim() || shortlistKeys.length >= 10}>Add</button>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 14 }}>
        <button className="btn primary" onClick={saveShortlist} disabled={reviewingShortlist || shortlistKeys.length === 0}>{reviewingShortlist ? 'Saving shortlist...' : 'Save reviewed shortlist'}</button>
        <button className="btn" onClick={() => setShortlistKeys(reviewCandidates.slice(0, 10).map((c: any, i: number) => candidateKey(c, i)))}>Reset to top 10</button>
        <span className="muted" style={{ fontSize: 13 }}>Saving locks this shortlist and unlocks Step 2 — running the forecasts (in the background; you can leave the page).</span>
      </div>
    </div>
  ) : null

  const evidenceReceiptsNode = (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>{isMonitorWorkspace ? 'Signal Receipts' : 'Evidence Receipts'}</h2>
      <p className="muted">{isMonitorWorkspace ? 'The read is backed by ranked signal checks, event probabilities, run references, and source coverage. Verified URL records are still required before sharing.' : 'Every ranking is backed by forecast questions, event probabilities, source records, and drill-down diagnostics. Internal build details stay collapsed below.'}</p>
      <div className="row" style={{ gap: 6, marginTop: 10 }}>
        <span className="pill">{primitiveItems.length || data?.question_set?.generated_count || 0} forecast questions</span>
        <span className="pill">{data?.question_set?.forecasted_count || 0} with event probabilities</span>
        {!isMonitorWorkspace ? <span className="pill">{dataBasis.activation_snapshot_count != null ? `${dataBasis.activation_snapshot_count || 0} frozen activation snapshots` : `${preparation?.benchmark_count || dataBasis.benchmark_count || 0} benchmarks`}</span> : null}
        <span className="pill">{verifiedSourceRecords || agentSourceLabels || 0} {verifiedSourceRecords ? 'verified sources' : agentSourceLabels ? 'source labels' : 'sources'}</span>
      </div>
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {fallbackEvidenceRefs.slice(0, 5).map((ref: any, i: number) => (
          <div key={`${ref?.set_item_id || ref?.run_id || i}`} className="workspace-question-row">
            <span className="pill ok">{ref?.ticker || ref?.rank || i + 1}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 750 }}>
                {forecastItemHref(ref, itemBySetItemId) ? <Link to={forecastItemHref(ref, itemBySetItemId)} state={workspaceFrom ? { from: workspaceFrom } : undefined}>{forecastQuestionLabel(ref)}</Link> : forecastQuestionLabel(ref)}
              </div>
              {ref?.question_text ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{clientFacingText(ref.question_text)}</div> : null}
              <div className="row" style={{ gap: 6, marginTop: 6 }}>
                <span className="pill">Event probability {pct(ref?.probability)}</span>
                {forecastItemHref(ref, itemBySetItemId) ? <Link className="pill" to={forecastItemHref(ref, itemBySetItemId)} state={workspaceFrom ? { from: workspaceFrom } : undefined}>Open forecast</Link> : null}
              </div>
            </div>
          </div>
        ))}
      </div>
      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 800 }}>Audit details and source records</summary>
        {!sourceRecords.length && agentSourceLabels ? (
          <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>
            Forecast runs cited {agentSourceLabels} source label{agentSourceLabels === 1 ? '' : 's'}, but no verified URL/source records are attached yet. This is why the workspace is ready for your use but not yet ready to share externally.
          </div>
        ) : null}
        {sourceRecords.length ? (
          <div style={{ marginTop: 14 }}>
            <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 850 }}>Source Records</div>
            <div className="workspace-question-list" style={{ marginTop: 8 }}>
              {sourceRecords.slice(0, 8).map((src: any, i: number) => {
                const href = src?.source_type === 'url' ? String(src?.input_value || '').trim() : ''
                const label = String(src?.source_label || src?.input_value || `Source ${i + 1}`).trim()
                return (
                  <div key={src?.source_asset_id || i} className="workspace-question-row">
                    <span className="pill">{src?.source_type || 'source'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{href ? <a href={href} target="_blank" rel="noreferrer">{label}</a> : label}</div>
                      {src?.ingested_at ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>Ingested {fmtDate(src.ingested_at)}</div> : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
        <div className="workspace-question-list" style={{ marginTop: 12 }}>
          {fallbackEvidenceRefs.slice(5, 15).map((ref: any, i: number) => (
            <div key={`${ref?.set_item_id || ref?.run_id || i}-more`} className="workspace-question-row">
              <span className="pill">{ref?.ticker || ref?.rank || i + 6}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 750 }}>{forecastQuestionLabel(ref)}</div>
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  <span className="pill">Event probability {pct(ref?.probability)}</span>
                  {forecastItemHref(ref, itemBySetItemId) ? <Link className="pill" to={forecastItemHref(ref, itemBySetItemId)} state={workspaceFrom ? { from: workspaceFrom } : undefined}>Open forecast</Link> : null}
                </div>
              </div>
            </div>
          ))}
        </div>
        <Link className="btn" style={{ marginTop: 12, display: 'inline-flex' }} to={`/question-sets/${data?.set_id}`}>Open method view</Link>
      </details>
    </div>
  )

  const qualityFlags = Array.isArray(summary?.workspace_quality_flags) ? summary.workspace_quality_flags : []
  const qualityFlagsNode = qualityFlags.length ? (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Build quality flags</h2>
      <p className="muted">Where this workspace fell back or needs review. These don't block the read, but lower confidence until resolved.</p>
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {qualityFlags.map((f: any, i: number) => {
          const sev = String(f?.severity || 'info').toLowerCase()
          const cls = sev === 'high' ? 'pill bad' : sev === 'warn' ? 'pill warn' : 'pill'
          return (
            <div key={`${f?.code || i}`} className="workspace-question-row">
              <span className={cls}>{sev}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 750 }}>{String(f?.stage || 'workspace')} · {String(f?.code || 'flag').replaceAll('_', ' ')}</div>
                {f?.message ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{String(f.message)}</div> : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  ) : null

  const universeCuration = Array.isArray(summary?.workspace_universe_curation) ? summary.workspace_universe_curation : []
  const universeCurationNode = universeCuration.length ? (
    <div className="card" style={{ marginTop: 16 }}>
      <h2>Universe curation</h2>
      <p className="muted">Names deliberately excluded from the ranking for cause. These are curation decisions, not quality issues — they don't lower confidence in the read.</p>
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {universeCuration.map((c: any, i: number) => (
          <div key={`${c?.code || i}`} className="workspace-question-row">
            <span className="pill">curated</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 750 }}>{String(c?.stage || 'prep')} · {String(c?.code || 'curation').replaceAll('_', ' ')}</div>
              {c?.message ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{String(c.message)}</div> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : null

  const methodHistoryNode = (
    <details className="card" style={{ marginTop: 16 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 800 }}>Method and workspace history</summary>
      <div className="workspace-draft-grid" style={{ marginTop: 14 }}>
        <div className="workspace-driver">
          <strong>Build summary</strong>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            <span className="pill">{isMonitorWorkspace ? 'Indicator map' : `Candidate screen: ${layer0?.top_10_shortlist?.length || 0} shortlisted`}</span>
            <span className="pill">Forecasts: {forecastCount}/{totalChecks}</span>
            <span className="pill">Ranked: {isMonitorWorkspace ? rankedForecastQuestions.length || citationRefs.length : rankedEntities.length || topEntities.length}</span>
            <span className="pill">{activeMonitoring ? `Monitors: ${lifecycle?.active_monitor_count || 0}` : 'Monitoring not activated'}</span>
          </div>
          {scoreFormula ? <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>Ranking formula: {scoreFormula}</div> : null}
        </div>
        <div className="workspace-driver">
          <strong>Original drivers</strong>
          <div className="muted" style={{ marginTop: 6 }}>{draft?.drivers?.map((d: any) => d.name).join(', ') || 'n/a'}</div>
        </div>
      </div>
      {draft?.scoping_artifacts?.length && !layer0Ready ? (
        <button className="btn primary" style={{ marginTop: 12 }} onClick={runLayer0Scoping} disabled={scoping || scopingInFlight}>{(scoping || scopingInFlight) ? (isMonitorWorkspace ? 'Refreshing signal map...' : 'Refreshing candidates...') : (isMonitorWorkspace ? 'Refresh signal map' : 'Refresh candidate screen')}</button>
      ) : null}
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {(primitiveItems.length ? primitiveItems : draft?.forecast_questions || []).slice(0, 30).map((row: any, i: number) => {
          const spec = row.question_spec_json || row
          const output = row.forecast_output_json || {}
          const title = spec.question_text || spec.title
          const forecastHref = forecastItemHref(row)
          return (
            <div key={i} className="workspace-question-row">
              <div className="pill">{i + 1}</div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{forecastHref ? <Link to={forecastHref} state={workspaceFrom ? { from: workspaceFrom } : undefined}>{title}</Link> : title}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{spec.driver} · {spec.why_it_matters}</div>
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  {output.execution_state === 'forecasted' ? <span className="pill ok">Event probability {pct(output.probability)} · run {output.run_id}</span> : null}
                  {forecastHref ? <Link className="pill" to={forecastHref} state={workspaceFrom ? { from: workspaceFrom } : undefined}>View forecast</Link> : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </details>
  )

  // Living-dashboard refresh banner: last manual refresh + what moved in the
  // underlying data (from the incremental refresh), and whether the move is
  // large enough to warrant a full re-forecast.
  const refreshInfo = summary?.workspace_refresh && typeof summary.workspace_refresh === 'object' ? summary.workspace_refresh : null
  const refreshChanges = Array.isArray(refreshInfo?.history?.[0]?.changes) ? refreshInfo.history[0].changes : []
  const refreshNode = refreshInfo?.last_refreshed_at ? (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Data refresh</h2>
        <div className="row" style={{ gap: 6 }}>
          <Freshness label="Refreshed" iso={refreshInfo.last_refreshed_at} staleAfterMs={3 * 24 * 3600 * 1000} />
          {refreshInfo.reforecast_recommended ? <span className="pill warn">Reforecast recommended</span> : <span className="pill ok">Data current</span>}
        </div>
      </div>
      {refreshChanges.length ? (
        <div className="workspace-prob-bars" style={{ marginTop: 10 }}>
          {refreshChanges.slice(0, 5).map((c: any, i: number) => {
            const d = Number(c?.delta_pct || 0)
            const cls = d > 0 ? 'delta up' : d < 0 ? 'delta down' : 'delta'
            return (
              <div key={i} className="workspace-prob-bar-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                <span style={{ minWidth: 0 }}>{c?.ticker} · {String(c?.metric || '').replaceAll('_', ' ')}</span>
                <span className={cls}>{d > 0 ? '▲' : d < 0 ? '▼' : ''} {(Math.abs(d) * 100).toFixed(1)}%</span>
              </div>
            )
          })}
        </div>
      ) : <p className="muted" style={{ marginTop: 8 }}>No material data changes since the last refresh.</p>}
    </div>
  ) : null

  // Read section nodes (the calm daily view).
  const changesNode = (
    <div className="workspace-draft-grid">
      <WorkspaceWhatChanged artifact={whatChanged} graphChange={graphChangeSummary} />
      <WorkspaceMonitoringTimeline
        lifecycle={lifecycle}
        artifact={whatChanged}
        canActivateMonitoring={canActivateMonitoring}
        executing={executing}
        onActivate={activateMonitoring}
      />
    </div>
  )

  // Whether any ranked entity actually has the revenue/margin/rerating triplet
  // (financial entity ranking). If not, fall back to the card layout.
  const rankedHasProbCols = (topEntities || []).some((e: any) => ['revenue_probability', 'margin_probability', 'rerating_probability'].some((k) => typeof e?.[k] === 'number'))
  const directEventRanking = answerType === 'event_probability_ranking' || (topEntities || []).some((e: any) => e?.score_semantic_type === 'event_probability')
  const toggleRanked = (key: string) => setExpandedRanked((prev) => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })
  const rankedTableNode = !cockpitReady && topEntities?.length && rankedHasProbCols ? (
    <div className="workspace-rank-table">
      <div className="workspace-rank-head">
        <span>#</span><span>Company</span><span>{directEventRanking ? 'Probability' : 'Score'}</span>
        <span>Revenue</span><span>Margin</span><span>Rerating</span>
        <span>Read</span><span aria-hidden> </span>
      </div>
      {topEntities.slice(0, 12).map((entity: any, i: number) => {
        const key = String(entity.ticker || entity.entity_name || i)
        const thesis = thesisCards.find((t: any) => String(t?.ticker || '') === String(entity?.ticker || '')) || {}
        const refs = entity.primitive_refs || thesis.primitive_refs || []
        const open = expandedRanked.has(key)
        return (
          <React.Fragment key={key}>
            <button type="button" className="workspace-rank-row" onClick={() => toggleRanked(key)} aria-expanded={open}>
              <span className="workspace-rank-num">{entity.rank || i + 1}</span>
              <span className="workspace-rank-name">
                <strong>{entity.entity_name || entity.ticker}</strong>
                <small className="muted">{entity.ticker}</small>
              </span>
              <span className="workspace-rank-score">{fmtRankedValue(entity.event_probability ?? entity.opportunity_score, entity.score_semantic_type)}</span>
              <span><ProbBar prob={entity.revenue_probability} /></span>
              <span><ProbBar prob={entity.margin_probability} /></span>
              <span><ProbBar prob={entity.rerating_probability} /></span>
              <span className="pill" style={{ justifySelf: 'start' }}>{statusLabel(entity.bucket)}</span>
              <span className="workspace-rank-caret" aria-hidden>{open ? '▾' : '▸'}</span>
            </button>
            {open ? (
              <div className="workspace-rank-detail">
                {thesis.underrecognized_mechanism ? <p className="muted" style={{ marginTop: 0 }}>{thesis.underrecognized_mechanism}</p> : null}
                {refs.length ? (
                  <div className="workspace-question-list">
                    {refs.slice(0, 6).map((ref: any, idx: number) => (
                      <div key={idx} className="workspace-question-row">
                        <span className="pill">{pct(ref.probability)}</span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div>{forecastItemHref(ref, itemBySetItemId) ? <Link to={forecastItemHref(ref, itemBySetItemId)} state={workspaceFrom ? { from: workspaceFrom } : undefined}>{forecastQuestionLabel(ref)}</Link> : forecastQuestionLabel(ref)}</div>
                          {ref.run_id ? <div className="muted" style={{ fontSize: 12 }}>run {ref.run_id}</div> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="muted" style={{ fontSize: 13 }}>No drill-down checks attached.</div>}
              </div>
            ) : null}
          </React.Fragment>
        )
      })}
    </div>
  ) : null

  const rankedNode = !cockpitReady && topEntities?.length && !rankedHasProbCols ? (
    <div className="workspace-candidate-grid">
      {topEntities.slice(0, 6).map((entity: any, i: number) => {
        const thesis = thesisCards.find((t: any) => String(t?.ticker || '') === String(entity?.ticker || '')) || {}
        const refs = entity.primitive_refs || thesis.primitive_refs || []
        return (
          <div key={`${entity.ticker || i}`} className="workspace-candidate-card">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div>
                <div className="pill ok">#{entity.rank || i + 1}</div>
                <h2>{entity.entity_name || entity.ticker}</h2>
                <div className="muted">{entity.ticker} · {statusLabel(entity.bucket)}</div>
              </div>
              <div className="workspace-score">{fmtRankedValue(entity.event_probability ?? entity.opportunity_score, entity.score_semantic_type)}</div>
            </div>
            {thesis.underrecognized_mechanism ? <p className="muted">{thesis.underrecognized_mechanism}</p> : null}
            {refs.length ? (
              <div className="workspace-ref-list">
                {refs.slice(0, 3).map((ref: any, idx: number) => (
                  <div key={idx} className="workspace-ref-row">
                    <span>{forecastQuestionLabel(ref)}</span>
                    <strong>Event probability {pct(ref.probability)}</strong>
                    <small>run {ref.run_id}</small>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  ) : null

  const watchNode = !cockpitReady ? (
    <div className="card">
      <h2>What To Watch</h2>
      {watchItems.length ? (
        <div className="workspace-driver-list" style={{ marginTop: 12 }}>
          {watchItems.slice(0, 5).map((item: string, i: number) => (
            <div key={i} className="workspace-driver"><strong>{i === watchItems.length - 1 && (item.toLowerCase().startsWith('risk watch') || item.toLowerCase().startsWith('falsifier')) ? 'Weakening condition' : `Watch ${i + 1}`}</strong><div className="muted" style={{ marginTop: 3 }}>{clientSignalLabel(item)}</div></div>
          ))}
        </div>
      ) : null}
      {monitorPreview.length ? (
        <div className="workspace-question-list" style={{ marginTop: 12 }}>
          {monitorPreview.map((m: any, i: number) => (
            <div key={i} className="workspace-question-row">
              <span className="pill">{m.ticker || m.target_type || 'monitor'}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{m.title || m.entity_name || m.driver || 'Source monitor'}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{fillEntityPlaceholders(m.monitor_rule, m.entity_name || m.title, m.ticker)}</div>
                {m.next_run_at ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>Next check: {fmtDate(m.next_run_at)}</div> : null}
              </div>
            </div>
          ))}
        </div>
      ) : !watchItems.length ? (
        <div className="workspace-driver-list">
          {draft?.watchpoints?.slice(0, 5).map((w: any, i: number) => (
            <div key={i} className="workspace-driver"><strong>{w.title}</strong><div className="muted" style={{ marginTop: 3 }}>{w.why_it_matters}</div></div>
          ))}
        </div>
      ) : null}
    </div>
  ) : null

  const earlyNode = earlySignals.length ? (
    <div className="card">
      <h2>Early Signals</h2>
      <p className="muted">Analyst chatter, X/Twitter, Substack, and rumor-style signals are shown separately. They are weak early-warning evidence until confirmed by filings, guidance, transcripts, company statements, or multiple independent sources.</p>
      <div className="workspace-question-list" style={{ marginTop: 12 }}>
        {earlySignals.slice(0, 12).map((signal: any, i: number) => {
          const href = String(signal?.url || '').startsWith('http') ? String(signal.url) : ''
          return (
            <div key={`${signal?.ticker || ''}-${signal?.url || i}`} className="workspace-question-row">
              <span className={signal?.is_rumor ? 'pill bad' : 'pill'}>{signal?.is_rumor ? 'rumor' : 'signal'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 750 }}>{signal?.ticker ? `${signal.ticker}: ` : ''}{signal?.claim || 'Early signal'}</div>
                {signal?.snippet ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{cleanSnippet(signal.snippet, 260)}</div> : null}
                <div className="row" style={{ gap: 6, marginTop: 6 }}>
                  {signal?.credibility ? <span className="pill">{signal.credibility} credibility</span> : null}
                  {signal?.source_domain ? <span className="pill">{signal.source_domain}</span> : null}
                  {href ? <a className="pill" href={href} target="_blank" rel="noreferrer">Open source</a> : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  ) : null

  const readSectionDefs = ([
    refreshNode ? { key: 'refresh', title: 'Data refresh', node: refreshNode } : null,
    { key: 'changes', title: 'What changed & monitoring', node: changesNode },
    (rankedTableNode || rankedNode) ? { key: 'ranked', title: 'Ranked entities', node: rankedTableNode || rankedNode } : null,
    watchNode ? { key: 'watch', title: 'What to watch', node: watchNode } : null,
    earlyNode ? { key: 'early', title: 'Early signals', node: earlyNode } : null,
  ].filter(Boolean) as Array<{ key: string; title: string; node: React.ReactNode }>)
  const orderedReadSections = applyLayoutOrder(readSectionDefs, layout.order)
  const orderedReadKeys = orderedReadSections.map((s) => s.key)

  return (
    <div className="container">
      <Topbar subtitle="Forecast Workspace" right={topbarRight} />
      {error && <ErrorBox error={error} />}
      {/* Stuck / never-built / being-reworked workspaces say so up front,
          instead of rendering as a page that quietly never progresses. */}
      <WorkspaceStateNotice state={(data as any)?.client_state} variant="banner" />
      {!hasDraft && discovery && (
        <div className="workspace-hero" style={{ marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>Discovery workspace</div>
          <h1>{discovery.archetype?.label || 'Forecast workspace discovery'}</h1>
          <p className="muted">{discovery.original_question}</p>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            <span className="pill">{discovery.stage}</span>
            {discovery.scenario_frame?.type && <span className="pill">{discovery.scenario_frame.type}</span>}
            {discovery.horizon && <span className="pill ok">{discovery.horizon}</span>}
          </div>
          {discovery.next_question && (
            <div className="workspace-section" style={{ marginTop: 14 }}>
              <h2>Next Clarification</h2>
              <p>{discovery.next_question.question}</p>
              {Array.isArray(discovery.next_question.options) && discovery.next_question.options.length ? (
                <div className="row" style={{ gap: 8, marginTop: 10 }}>
                  {discovery.next_question.options.map((option: any) => (
                    <button key={String(option)} className={discoveryAnswerValue === String(option) ? 'btn primary' : 'btn'} onClick={() => setDiscoveryAnswerValue(String(option))}>{String(option)}</button>
                  ))}
                </div>
              ) : (
                <input className="input" style={{ marginTop: 10 }} value={discoveryAnswerValue} onChange={(e) => setDiscoveryAnswerValue(e.target.value)} placeholder="Type your response" />
              )}
              <div className="row" style={{ gap: 8, marginTop: 12 }}>
                <button className="btn primary" onClick={answerDiscoveryQuestion} disabled={planning || !discoveryAnswerValue.trim()}>{planning ? 'Saving...' : 'Save response'}</button>
                <button className="btn" onClick={compileDiscoveryPlan} disabled={planning}>{planning ? 'Compiling plan...' : 'Compile with current responses'}</button>
              </div>
            </div>
          )}
          {!discovery.next_question && (
            <div className="workspace-section" style={{ marginTop: 14 }}>
              <button className="btn primary" onClick={compileDiscoveryPlan} disabled={planning}>
                {planning ? 'Compiling plan...' : 'Compile current plan'}
              </button>
            </div>
          )}
        </div>
      )}

      {hasDraft && draft && (
        <>
          <div className="workspace-hero" style={{ marginTop: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>{heroKicker}</div>
              <h1>{displayTitle}</h1>
              <p className="muted">{displaySubtitle}</p>
              {draft.original_question ? <div className="workspace-original-question"><strong>Original question</strong><span>{draft.original_question}</span></div> : null}
            </div>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
              <span className="pill ok">{draft.horizon}</span>
              {forecastCount ? <span className="pill">{forecastCount} forecasts</span> : null}
              {readinessReady ? <span className={externalReady ? 'pill ok' : 'pill'}>{externalReady ? 'ready to share' : 'ready'}</span> : null}
              {lifecycle?.active_monitor_count ? <span className="pill ok">{lifecycle.active_monitor_count} monitors</span> : null}
              <Freshness
                label="Last updated"
                iso={latestWorkspaceUpdateAt}
                staleAfterMs={24 * 3600 * 1000}
              />
            </div>
            </div>

            {layer3?.client_answer && (
              <div className="workspace-answer-panel">
                <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>Current read</div>
                <h2>{cockpitReady ? cockpit?.answer_brief?.headline : layer3.client_answer.headline}</h2>
                <p>{cockpitReady ? cockpit?.answer_brief?.executive_answer : layer3.client_answer.executive_answer}</p>
                {cockpitReady ? (
                  <WorkspaceCockpit cockpit={cockpit} itemBySetItemId={itemBySetItemId} workspaceFrom={workspaceFrom} companyIntelRows={companyIntelRows} />
                ) : rankedForecastQuestions.length ? (
                  <div className="workspace-indicator-grid">
                    <div className="workspace-indicator-card primary">
                      <span>Priority monitor</span>
                      <strong>{forecastQuestionLabel(topSignal)}</strong>
                      <small>Event probability {pct(topSignal?.probability)} · source-backed forecast question</small>
                      {forecastItemHref(topSignal, itemBySetItemId) ? <Link to={forecastItemHref(topSignal, itemBySetItemId)} state={workspaceFrom ? { from: workspaceFrom } : undefined}>Open forecast</Link> : null}
                    </div>
                    <div className="workspace-indicator-card">
                      <span>High-likelihood events</span>
                      <strong>{strongSignalCount}</strong>
                      <small>Event-likelihood forecasts above 60%; this is not source coverage or sharing state</small>
                    </div>
                    <div className="workspace-indicator-card">
                      <span>What would weaken this</span>
                      <strong>{topFalsifier ? pct(topFalsifier.probability) : 'none'}</strong>
                      <small>{topFalsifier ? `Event probability for ${forecastQuestionLabel(topFalsifier)}` : 'No weakening-condition test detected in the top signal map'}</small>
                    </div>
                    <div className="workspace-indicator-card">
                      <span>Source base</span>
                      <strong>{verifiedSourceRecords || agentSourceLabels || 0}</strong>
                      <small>{sourceState} - use source coverage and agreement separately from event probabilities</small>
                    </div>
                    <div className="workspace-indicator-card">
                      <span>Refresh state</span>
                      <strong>{monitorState}</strong>
                      <small>{activeMonitoring ? (lifecycle?.next_monitor_run_at ? `Next check ${fmtDate(lifecycle.next_monitor_run_at)}` : 'Live monitor refreshes enabled') : 'Activate monitoring to turn this into a living dashboard'}</small>
                    </div>
                  </div>
                ) : null}
                {keyTakeaways.length ? (
                  <div className="workspace-takeaway-list">
                    {keyTakeaways.slice(0, 4).map((item: string, i: number) => (
                      <div key={i} className="workspace-takeaway"><strong>{i + 1}</strong><span>{item}</span></div>
                    ))}
                  </div>
                ) : null}
                <div className="workspace-stat-strip">
                  <div><strong>{dataBasis.primitive_count || data?.question_set?.forecasted_count || 0}</strong><span>event-probability forecasts</span></div>
                  {isMonitorWorkspace ? <div><strong>{rankedForecastQuestions.length || citationRefs.length || 0}</strong><span>ranked signal checks</span></div> : <div><strong>{dataBasis.entity_count || rankedEntities.length || 0}</strong><span>ranked entities</span></div>}
                  {!isMonitorWorkspace ? <div><strong>{dataBasis.activation_snapshot_count ?? dataBasis.benchmark_count ?? 0}</strong><span>{dataBasis.activation_snapshot_count != null ? 'frozen activation snapshots' : 'benchmarks'}</span></div> : null}
                  <div><strong>{verifiedSourceRecords || agentSourceLabels || 0}</strong><span>{verifiedSourceRecords ? 'verified source records' : agentSourceLabels ? 'source labels only' : 'source records'}</span></div>
                  <div><strong>{lifecycle?.active_monitor_count || 0}</strong><span>{activeMonitoring ? 'live monitors' : 'monitors not activated'}</span></div>
                </div>
                <WorkspaceSignalSemantics
                  forecastCount={forecastCount}
                  totalChecks={totalChecks}
                  verifiedSourceRecords={Number(verifiedSourceRecords || 0)}
                  agentSourceLabels={Number(agentSourceLabels || 0)}
                  readinessReady={readinessReady}
                  externalReady={externalReady}
                  activeMonitoring={activeMonitoring}
                  latestUpdateAt={latestWorkspaceUpdateAt}
                  readinessScore={readinessScore}
                />
                {publicationReview?.recommendation && !externalReady ? <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>{publicationReview.recommendation}</div> : null}
                {lifecycle?.next_monitor_run_at ? <div className="pill ok" style={{ marginTop: 12 }}>Next refresh check: {fmtDate(lifecycle.next_monitor_run_at)}</div> : null}
              </div>
            )}

            {!activeMonitoring && !canActivateMonitoring && (
            <div className="workspace-section" style={{ marginTop: 16 }}>
              {scopingFailedError ? (
                <div className="pill bad" style={{ marginBottom: 10 }}>Signal map preparation failed: {scopingFailedError}</div>
              ) : null}
              <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                {!layer0Ready ? (
                  <button className="btn" onClick={runLayer0Scoping} disabled={scoping || scopingInFlight}>{(scoping || scopingInFlight) ? 'Preparing signals...' : scopingFailedError ? 'Retry signal map' : 'Prepare signal map'}</button>
                ) : null}
                {!canActivateMonitoring && !layer3?.client_answer ? <button className="btn primary" onClick={isBatchPipelineOrg ? runBatchPipeline : materializeWorkspace} disabled={executing || workspaceExecution?.status === 'batch_submitted' || requiresCandidateReview}>
                  {executing ? 'Starting forecasts...' : workspaceExecution?.status === 'batch_submitted' ? 'Forecasts running' : workspaceExecution?.status === 'layer_1_batch_ready' ? 'Submit source-backed forecasts' : requiresCandidateReview ? 'Approve shortlist first (Step 1)' : opportunitySpaceResearch ? 'Run signal checks' : 'Step 2 · Run forecasts →'}
                </button> : null}
                <span className="muted" style={{ fontSize: 13 }}>
                  {layer3?.client_answer ? 'The workspace read exists, but activation is waiting on completed forecasts or review.' : requiresCandidateReview ? 'Step 1: review and save the shortlist above — that unlocks the forecast run here.' : workspaceExecution?.status === 'layer_1_batch_ready' ? 'Forecast questions exist, but no batch has been submitted yet. Submit only after source and resolution gates pass.' : isMonitorWorkspace ? 'Creates externally resolvable signal checks and starts the forecasting run — runs in the background, you can leave the page.' : opportunitySpaceResearch ? 'Candidate discovery is still thin, so Cenva will first test whether the opportunity and public-company exposure are externally measurable before ranking companies.' : 'Runs source-backed forecasts on the approved companies and builds the read — in the background; you can leave the page and it fills in as forecasts land.'}
                </span>
              </div>
            </div>
            )}

            {execution && !canActivateMonitoring && (
              <div className="pill" style={{ marginTop: 10 }}>
                {execution.pipeline?.batch_id
                  ? `Forecasts started · ${execution.pipeline.request_count} analysis requests`
                  : (execution as any)?.forecast_job_count
                    ? `Forecasts started · ${(execution as any).forecast_job_count} of ${execution.generated_count} questions queued`
                    : `Created ${execution.generated_count} forecast questions · forecasts not queued yet`}
              </div>
            )}
          </div>

          {/* Read vs Build & Method, with lightweight personalization. */}
          <div className="workspace-toolbar">
            <WorkspaceViewTabs view={view} onChange={setView} buildNeedsAttention={requiresCandidateReview} />
            {view === 'read' ? (
              <button className="btn" onClick={() => setCustomizing((c) => !c)}>
                {customizing ? 'Done' : 'Customize'}
              </button>
            ) : null}
          </div>

          {view === 'read' ? (
            <>
              <WorkspaceAskBox workspaceId={workspaceId || ''} watchlistId={data?.watchlist_id} workspaceTitle={displayTitle} />
              {(note || customizing) ? (
                <div className="card workspace-note">
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 850 }}>Your note</div>
                  {customizing ? (
                    <textarea className="input" style={{ marginTop: 8, minHeight: 70 }} value={note} onChange={(e) => persistNote(e.target.value)} placeholder="Your thesis, what you're watching for, reminders to yourself." />
                  ) : (
                    <p style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{note}</p>
                  )}
                </div>
              ) : null}

              {customizing ? (
                <div className="muted workspace-customize-hint">
                  Reorder or hide sections below. Your layout is saved on this device.
                  {(layout.order.length || layout.hidden.length) ? <button className="btn" style={{ marginLeft: 8 }} onClick={resetLayout}>Reset layout</button> : null}
                </div>
              ) : null}

              {orderedReadSections.map((section) => {
                const sectionHidden = layout.hidden.includes(section.key)
                if (sectionHidden && !customizing) return null
                return (
                  <div key={section.key} className={sectionHidden ? 'workspace-read-section is-hidden' : 'workspace-read-section'}>
                    {customizing ? (
                      <div className="workspace-section-controls">
                        <span className="muted">{section.title}{sectionHidden ? ' (hidden)' : ''}</span>
                        <div className="row" style={{ gap: 4 }}>
                          <button className="btn" onClick={() => moveSection(orderedReadKeys, section.key, -1)} aria-label="Move section up">Up</button>
                          <button className="btn" onClick={() => moveSection(orderedReadKeys, section.key, 1)} aria-label="Move section down">Down</button>
                          <button className="btn" onClick={() => toggleSectionHidden(section.key)}>{sectionHidden ? 'Show' : 'Hide'}</button>
                        </div>
                      </div>
                    ) : null}
                    {section.node}
                  </div>
                )
              })}
            </>
          ) : (
            <>
              {qualityFlagsNode}
              {universeCurationNode}
              {signalMapNode}
              {candidateReviewNode}
              {evidenceReceiptsNode}
              {methodHistoryNode}
            </>
          )}
        </>
      )}
    </div>
  )
}
